// ═══ AUREUS SOCIAL PRO — Moteur de calcul paie ═══
// Extrait du monolithe pour reutilisation dans les modules
// lib isomorphique — pas de "use client"

import { LOIS_BELGES, TX_ONSS_W, TX_ONSS_E, TX_OUV108, TX_AT_ASSURANCE, PV_DOUBLE, SAISIE_2026_TRAVAIL, SAISIE_2026_REMPLACEMENT, SAISIE_IMMUN_ENFANT_2026, AF_REGIONS, TAUX_SECTORIELS, getBonusEmploiFiscal } from "@/app/lib/lois-belges";
import { cotisationFondsSE, tauxFondsSE, blocFondsSE, pensionSectorielleOnss, cotisationPensionSectorielle } from '@/app/lib/fonds-se-cotisation';

// Réduction « cotisations sociales » forfaitaire des dirigeants d'entreprise (statut social
// indépendant) déduite de la rémunération brute mensuelle AVANT frais forfaitaires, pour le
// calcul du précompte professionnel. Barème officiel : LB.pp.reductionCotisSocialesDirigeant.
// Source : SPF Finances, Formule-clé 01/01/2026 (ESS-SR/2025-0928, n°7 1°). Réduction arrondie
// à 2 décimales comme le simulateur officiel SPF (case E : ROUND(...,2)).
export function calcReductionCotisSocialesDirigeant(brutMensuel) {
  const cfg = LOIS_BELGES.pp && LOIS_BELGES.pp.reductionCotisSocialesDirigeant;
  const paliers = cfg && cfg.paliers;
  if (!paliers || !(brutMensuel > 0)) return 0;
  for (const p of paliers) {
    if (brutMensuel <= p.max) {
      return Math.round((p.base + p.taux * (brutMensuel - p.seuil)) * 100) / 100;
    }
  }
  return 0;
}

export function calcPrecompteExact(brutMensuel, options) {
  const opts = options || {};
  const situation = opts.situation || 'isole';
  const enfants = +(opts.enfants || 0);
  const enfantsHandicapes = +(opts.enfantsHandicapes || 0);
  const handicape = !!opts.handicape;
  const conjointHandicape = !!opts.conjointHandicape;
  // Revenus professionnels propres du conjoint / cohabitant légal — TRI-ÉTAT.
  // Avant le 2026-07-26 : `!!opts.conjointRevenus`, donc `undefined` → false →
  // « le partenaire n'a aucun revenu » PRÉSUMÉ pour tout le monde. Or personne
  // ne passait ce paramètre : la présomption s'appliquait à 100 % des cas.
  //   true      → le partenaire A des revenus professionnels  → barème I
  //   false     → déclaré SANS revenus professionnels          → barème II
  //   null      → INCONNU (aucune déclaration)                 → barème I
  const conjointRevenus = (opts.conjointRevenus === undefined || opts.conjointRevenus === null)
    ? null
    : !!opts.conjointRevenus;
  const conjointRevenuLimite = !!opts.conjointRevenuLimite;
  const conjointPensionLimitee = !!opts.conjointPensionLimitee;
  // n°33 de l'Annexe III (AR 11/12/2025, revenus 2026 — texte primaire lu le 04/09/2026,
  // cf. lois-belges.js pp.plafondPensionConjointBaremeII) : conjoint dont les revenus sont
  // EXCLUSIVEMENT des pensions/rentes sous le plafond → barème II (quotient conjugal), SANS
  // la réduction 40.3. Le drapeau est posé par precompte-conjoint.js (source unique), jamais
  // déduit ici. Additif : défaut false → comportement inchangé.
  const conjointPensionBaremeII = !!opts.conjointPensionBaremeII;
  const parentIsole = !!opts.parentIsole;
  const personnes65 = +(opts.personnes65 || 0);
  const autresCharges = +(opts.autresCharges || 0);
  const dirigeant = !!opts.dirigeant;
  const taxeCom = (opts.taxeCom !== undefined && opts.taxeCom !== null ? +opts.taxeCom : 7) / 100;
  const regime = +(opts.regime || 100) / 100;
  // Non-résident NE remplissant PAS la condition (contrat année complète + ≥75% du temps
  // de travail légal) → BARÈME III : précompte calculé SANS tenir compte de la situation
  // personnelle/familiale → aucune réduction (y compris quotité exemptée) ni quotient conjugal.
  // Source : SPF Finances, précompte non-résidents ; barème III = tranches PP 2026 sans réduction.
  // (Additif : défaut false → comportement résident inchangé, validations 29/29 intactes.)
  const nonResidentBareme3 = !!opts.nonResidentBareme3;
  // BARÈME II = marié / cohabitant légal DONT LE PARTENAIRE N'A PAS de revenus
  // professionnels propres. Ce n'est PAS un défaut, c'est une exception à
  // justifier (SPF Finances / Securex Lex4You, vérifié 2026-07-26).
  //   - 'marie_1r' encode déjà « 1 seul revenu » → barème II inconditionnel,
  //     comportement STRICTEMENT inchangé (validations 29/29 intactes).
  //   - 'cohabitant' exige désormais une déclaration explicite
  //     (`conjointRevenus: false`). Sans déclaration → barème I.
  // Avant : `!conjointRevenus` — `undefined` passait pour « pas de revenus » →
  // jusqu'à 495 €/mois de précompte NON retenu, à charge du travailleur au
  // décompte IPP. Le générateur de fiche (fiche-paie-generator.js:58) et
  // prestations-paie appliquaient déjà le barème I : ce fix les RÉCONCILIE.
  const isBareme2 = !nonResidentBareme3 && (
    situation === 'marie_1r' ||
    (situation === 'cohabitant' && conjointRevenus === false) ||
    conjointPensionBaremeII // n°33 (05/09/2026)
  );

  const brut = brutMensuel * regime;
  if (brut <= 0) return { pp: 0, rate: 0, forfait: 0, reduction: 0, bonusEmploi: 0, detail: {} };

  // 1. ONSS travailleur 13.07% — un DIRIGEANT d'entreprise ne cotise PAS à l'ONSS
  //    salarié : il cotise à l'INASTI séparément (versements trimestriels indépendant),
  //    jamais retenu sur sa rémunération. Sa base imposable = brut, sans werkbonus
  //    (réduction réservée aux travailleurs salariés). Gate `dirigeant` : le chemin
  //    salarié (dirigeant=false) reste STRICTEMENT inchangé (validations 29/29 intactes).
  //    CORR 2026-07-20 : avant, la base dirigeant était amputée de ~13,07% (ONSS retenu à
  //    tort) → précompte sous-estimé (cas Solinas +83€/mois, écart croissant avec le brut).
  // ── L'ONSS RÉELLEMENT RETENU PEUT ÊTRE FOURNI PAR L'APPELANT (02/09/2026) ──
  // Même idiome que `werkbonusSocial` juste en dessous : `null`/absent → on
  // calcule, une valeur → on l'utilise. Additif, donc le comportement par
  // défaut ne bouge pas d'un centime et les fiches certifiées non plus.
  //
  // POURQUOI C'EST NÉCESSAIRE. Cette fonction attend un BRUT et déduit l'ONSS
  // elle-même. Sept statuts particuliers lui passaient un montant DÉJÀ NET
  // d'ONSS (`brutImp`, `brutR - onssW`) : l'ONSS était donc déduit DEUX FOIS,
  // et le précompte sortait trop bas. C'est exactement le défaut qui avait été
  // repéré et corrigé pour le DIRIGEANT le 20/07/2026 — « sinon double
  // comptage → précompte trop bas », incident client à +83 €/mois. La cause a
  // été réparée sur cette surface-là et laissée sur les autres (R2 ter-C).
  //
  // ⚠️ ET « PASSER LE BRUT » NE SUFFIT PAS : un étudiant sous quota cotise à la
  // solidarité (2,71 %), pas à 13,07 %. Recalculer l'ONSS ici lui appliquerait
  // un taux qui n'est pas le sien. D'où cette option : l'appelant, qui connaît
  // le régime, donne le montant qu'il a réellement retenu.
  // 🔴 CORRIGÉ (précompte ouvrier, 108% oublié dans CE fallback) — l'une des
  // « autres surfaces » citées juste au-dessus : quand l'appelant NE fournit
  // PAS `onssRetenu` (cas réel : fiche-paie-generator.js:665, qui passe
  // `werkbonusSocial` mais jamais `onssRetenu`), ce fallback calculait
  // `brut × TX_ONSS_W` — l'ONSS travailleur PLAT, sans le ×108% obligatoire
  // pour les ouvriers (déclaration ONSS « rémunération portée à 108% »,
  // cf. calcCSSS juste plus bas, qui l'applique déjà correctement).
  // Base imposable trop haute → précompte trop haut. Mesuré sur une fiche
  // réelle (Groupe Ravone Invest, Luigia Usai, 08/2026) : 329,59 € retenus
  // au lieu de 318,86 € — 10,73 €/mois en trop, reproduit au centime en
  // rejouant ce chemin exact. `isOuvrier` était déjà un paramètre de cette
  // fonction (lu par le fallback `calcWerkbonus` juste en dessous) ; il ne
  // l'était pas ici. Additif : `onssRetenu` fourni → comportement inchangé ;
  // étudiant/dirigeant → `isOuvrier` vaut false, TX_OUV108 ne s'applique pas.
  const onss = dirigeant ? 0 : (opts.onssRetenu != null
    ? Math.round(+opts.onssRetenu * 100) / 100
    : Math.round(brut * (opts.isOuvrier ? TX_OUV108 : 1) * TX_ONSS_W * 100) / 100);
  // Base imposable = brut − ONSS RÉELLEMENT retenu (après bonus à l'emploi SOCIAL).
  // Le werkbonus social réduit la cotisation perso → AUGMENTE le revenu imposable
  // (sources officielles oxida/Acerta/Securex NL, 2026). CORR 2026-06-29 : avant on
  // prenait l'ONSS plein → précompte des bas salaires sous-estimé (zone werkbonus).
  // Le werkbonus FISCAL (calcBonusEmploi) reste appliqué ensuite en compensation.
  const wbSocial = dirigeant ? 0 : (opts.werkbonusSocial != null
    ? +opts.werkbonusSocial
    : calcWerkbonus(brut, !!opts.isOuvrier, regime));
  const onssNet = Math.max(0, Math.round((onss - wbSocial) * 100) / 100);
  // DIRIGEANT : à la place de l'ONSS salarié, la méthode officielle SPF déduit de la base
  // imposable une réduction « cotisations sociales » (INASTI) forfaitaire (formule-clé 2026,
  // n°7 1°) — la société ne retient pas l'INASTI, mais le SPF en tient compte dans la base.
  // Un montant réel peut être passé via opts.cotisSocialesDirigeant (sinon barème officiel).
  // CORR 2026-07-20 : sans cette déduction, le précompte dirigeant était SURÉVALUÉ (~615 vs
  // ~350 officiel pour 2612€ isolé) — vérifié contre le simulateur SPF (onglet Dirigeants).
  const cotisDirigeant = dirigeant
    ? (opts.cotisSocialesDirigeant != null
        ? Math.round(+opts.cotisSocialesDirigeant * 100) / 100
        : calcReductionCotisSocialesDirigeant(brut))
    : 0;
  const imposable = brut - onssNet - cotisDirigeant;

  // 2. Annualisation
  const annuel = imposable * 12;

  // 3. Frais professionnels forfaitaires 2026
  let forfaitAn = 0;
  if (dirigeant) {
    forfaitAn = Math.min(annuel * LOIS_BELGES.pp.fraisPro.dirigeant.pct, LOIS_BELGES.pp.fraisPro.dirigeant.max);
  } else {
    forfaitAn = Math.min(annuel * LOIS_BELGES.pp.fraisPro.salarie.pct, LOIS_BELGES.pp.fraisPro.salarie.maxPP||7269);
  }

  // 4. Revenu net imposable annuel
  const baseAnnuelle = Math.max(0, annuel - forfaitAn);

  // 5. Quotient conjugal (barème 2 uniquement)
  let qcAttribue = 0;
  let baseApresQC = baseAnnuelle;
  if (isBareme2) {
    qcAttribue = Math.min(baseAnnuelle * LOIS_BELGES.pp.quotientConjugal.pct, LOIS_BELGES.pp.quotientConjugal.max);
    baseApresQC = baseAnnuelle - qcAttribue;
  }

  // 6. Impôt progressif — Tranches PP SPF 2026 (Formule-clé Annexe III)
  const calcImpotProgressif = (base) => {
    if (base <= 0) return 0;
    const T=LOIS_BELGES.pp.tranches;let imp=0,prev=0;for(const t of T){const s=Math.min(base,t.max)-Math.max(prev,t.min);if(s>0)imp+=s*t.taux;prev=t.max;}return imp;
  };
  let impot = calcImpotProgressif(baseApresQC);

  // Impôt sur quotient conjugal (même barème progressif)
  if (isBareme2 && qcAttribue > 0) {
    impot += calcImpotProgressif(qcAttribue);
  }

  // 7. Tranche non imposable (formule-clé 2026 — Annexe III AR/CIR 92)
  // Remplace l'ancienne méthode quotité exemptée × taux marginal
  const tni = LOIS_BELGES.pp.trancheNonImposable || {};
  const redQE = isBareme2 ? (tni.bareme2 || 5975.96) : (tni.bareme1 || 2987.98);

  // 8. Réduction enfants à charge (montants annuels 2026)
  const tabEnfants = LOIS_BELGES.pp.reductionsEnfants;
  const suppEnfant = LOIS_BELGES.pp.reductionEnfantSupp;
  const enfTotal = enfants + enfantsHandicapes; // handicapés comptent double fiscalement
  const enfFiscaux = enfTotal + enfantsHandicapes; // double comptage
  let redEnfants = 0;
  if (enfFiscaux > 0) {
    if (enfFiscaux <= 8) redEnfants = tabEnfants[enfFiscaux];
    else redEnfants = tabEnfants[8] + (enfFiscaux - 8) * suppEnfant;
  }

  // 9. Réduction parent isolé avec enfants
  let redParentIsole = 0;
  if (parentIsole && enfTotal > 0) redParentIsole = LOIS_BELGES.pp.reductionParentIsole;

  // 10. Réduction bénéficiaire handicapé
  let redHandicape = 0;
  if (handicape) redHandicape = LOIS_BELGES.pp.reductionHandicape;

  // 11. Réduction conjoint handicapé (barème 2)
  let redConjHandicape = 0;
  if (isBareme2 && conjointHandicape) redConjHandicape = LOIS_BELGES.pp.reductionConjointHandicape;

  // 12. Réduction conjoint revenu limité (n°40.2) — 05/09/2026 : réservée à la SECTION 1re
  //     (barème I). Annexe III lue le 04/09/2026 : le n°40 ne joue pas sous le quotient
  //     conjugal ; avant, un dossier « marié, 1 revenu » + revenu limité du conjoint cumulait
  //     barème II ET 40.2 — deux avantages pour une même situation, précompte sous-retenu.
  let redConjRevLimite = 0;
  if (!isBareme2 && conjointRevenuLimite) redConjRevLimite = LOIS_BELGES.pp.reductionConjointRevenuLimite;

  // 13. Réduction conjoint pension limitée (n°40.3) — même section 1re ; sous le plafond
  //     n°33 le conjoint pensionné ouvre le barème II SANS cette réduction (voir isBareme2).
  let redConjPension = 0;
  if (!isBareme2 && conjointPensionLimitee) redConjPension = LOIS_BELGES.pp.reductionConjointPensionLimitee;

  // 14. Réduction personnes 65+ à charge
  let redP65 = personnes65 * LOIS_BELGES.pp.reductionPersonne65;

  // 15. Réduction autres personnes à charge
  let redAutres = autresCharges * LOIS_BELGES.pp.reductionAutreCharge;

  // Total réductions annuelles. Barème III (non-résident hors condition 75%) : AUCUNE
  // réduction (ni quotité exemptée, ni charges de famille) — art. barème III Annexe III.
  const totalReductions = nonResidentBareme3
    ? 0
    : redQE + redEnfants + redParentIsole + redHandicape + redConjHandicape + redConjRevLimite + redConjPension + redP65 + redAutres;

  // 6b. Impôt annuel après réductions
  const impotApresReduc = Math.max(0, impot - totalReductions);

  // 16. (CORRIGÉ 2026-06-26 — chantier précompte) PAS de réduction communale ici.
  // Les additionnels communaux (~7% moyen) sont DÉJÀ intégrés dans les TAUX du barème
  // (25% × 1,07 = 26,75% … — formule-clé Annexe III, SPF Finances). L'ancienne étape
  // × (300 − taxeCom)/300 les re-soustrayait → précompte ~2,3% trop bas. Validé contre
  // 5 fiches Securex réelles (mai 2026). taxeCom conservé en signature, sans effet ici.
  void taxeCom;
  const impotApresComm = Math.round(impotApresReduc * 100) / 100;

  // Bonus emploi fiscal (33.14% réduction sur bonus social ONSS)
  const bonusEmploi = 0; // calculé séparément si nécessaire

  // PP mensuel
  const ppMensuel = Math.round(impotApresComm / 12 * 100) / 100;

  // Taux effectif
  const taux = brut > 0 ? Math.round(ppMensuel / brut * 10000) / 100 : 0;

  return {
    pp: ppMensuel,
    rate: taux,
    forfait: Math.round(forfaitAn / 12 * 100) / 100,
    reduction: Math.round(totalReductions / 12 * 100) / 100,
    bonusEmploi,
    detail: {
      brut, onss, imposable, annuel, forfaitAn,
      baseAnnuelle, qcAttribue, baseApresQC,
      impotBrut: Math.round(impot * 100) / 100,
      redQE, redEnfants, redParentIsole, redHandicape,
      redConjHandicape, redConjRevLimite, redConjPension, redP65, redAutres,
      totalReductions: Math.round(totalReductions * 100) / 100,
      impotApresReduc: Math.round(impotApresReduc * 100) / 100,
      impotApresComm: impotApresComm,
      ppMensuel, taux,
      situation, enfants, enfantsHandicapes, dirigeant, isBareme2, nonResidentBareme3
    }
  };
}

export function calcCSSS(brutMensuel, situation, isOuvrier, brutTrimestrielReel) {
  // ═══ CSSS 2026 — Formule ONSS officielle ═══
  // Retenue MENSUELLE basée sur rém. TRIMESTRIELLE (pour déterminer la tranche)
  // et rém. MENSUELLE (pour le calcul du montant)
  // Source: socialsecurity.be — Instructions administratives ONSS 2026/2 « cotisation spéciale ».
  // ⚠️ OUVRIERS : la rémunération est PORTÉE À 108 % (× ouvrier108) — confirmé source primaire
  // ONSS « La rémunération du travailleur (portée à 108 %) », vérifié 2026-06-30. (Exception ONSS-APL.)
  const brut = isOuvrier ? Math.round(brutMensuel * LOIS_BELGES.onss.ouvrier108 * 100) / 100 : brutMensuel;
  // ⚠️ La TRANCHE se détermine sur la rémunération TRIMESTRIELLE RÉELLE (règle ONSS :
  // « cette cotisation doit être calculée sur la rémunération trimestrielle »).
  // `brut * 3` = défaut historique, qui SUPPOSE 3 mois pleins → FAUX pour toute entrée
  // (ou sortie) en cours de trimestre : le travailleur se retrouvait projeté dans une
  // tranche supérieure et se voyait retenir une CSSS NON DUE.
  // Bug constaté 2026-07-16 sur les 5 transférés du 22/06/2026 (7 jours au T2) :
  //   Shatz brut réel T2 = 1.717,97 (< seuil 3.285,29 → CSSS = 0)
  //   mais 1.717,97 × 3 = 5.153,91 → tranche 3.285,29-5.836,14 → forfait 15,45
  //   → 15,45 / 3 = 5,15 retenus À TORT sur son net (idem Kangouté, Rosero, Noury).
  // Contre-épreuve : Nourdin (2.220 × 3 mois pleins = 6.660 réels) → inchangé.
  // → Les appelants qui CONNAISSENT le trimestre réel passent `brutTrimestrielReel`.
  //   Sans lui : comportement historique strictement préservé (rétro-compatible).
  const brutTrim = (brutTrimestrielReel != null && Number.isFinite(Number(brutTrimestrielReel)))
    ? (isOuvrier
        ? Math.round(Number(brutTrimestrielReel) * LOIS_BELGES.onss.ouvrier108 * 100) / 100
        : Number(brutTrimestrielReel))
    : brut * 3;
  const isole = !situation || situation === 'isole';
  // 3 régimes ONSS distincts (vérifié 2026-07-02) : isolé (plafond 182,82) · ménage 1 revenu
  // = conjoint SANS revenus professionnels (marie_1r — plafond 182,82, pas de forfait bas) ·
  // ménage 2 revenus (plafond 154,92). Avant : marie_1r tombait à tort dans menage2revenus.
  const menage1 = situation === 'marie_1r';
  const csssConfig = isole ? LOIS_BELGES.csss.isole
    : (menage1 ? (LOIS_BELGES.csss.menage1revenu || LOIS_BELGES.csss.menage2revenus) : LOIS_BELGES.csss.menage2revenus);
  
  if (!csssConfig || !csssConfig.tranches) {
    // Fallback simplifié si config manquante
    if (brutTrim <= 6570.54) return 0;
    if (brutTrim <= 11211.00) return Math.round((30.99 / 3 + 0.011 * Math.max(0, brut - 2190.18)) * 100) / 100;
    return Math.round(182.82 / 3 * 100) / 100;
  }

  var tranches = csssConfig.tranches;
  for (var i = 0; i < tranches.length; i++) {
    var t = tranches[i];
    if (brutTrim > t.trimMin && brutTrim <= t.trimMax) {
      var base = (t.forfaitTrim || 0) / 3;
      var surplus = t.mensuelTaux > 0 ? t.mensuelTaux * Math.max(0, brut - (t.mensuelSeuil || 0)) : 0;
      var csss = base + surplus;
      if (t.minTrim) csss = Math.max(csss, t.minTrim / 3); // « avec un minimum de X EUR par trimestre » (ONSS)
      if (t.maxTrim) csss = Math.min(csss, t.maxTrim / 3);
      return Math.round(csss * 100) / 100;
    }
  }
  return 0;
}

// ── Bonus emploi volet B (fiscal — réduction PP) ─────────────────────────────
// ═══ BONUS À L'EMPLOI — modèle officiel 2 volets (ONSS 01/04/2026) ═══════════
const _r2 = (x) => Math.round(x * 100) / 100;

// Volets A & B AVANT écrêtement. S = rémunération mensuelle de référence (100%, temps plein).
// Palier de barème werkbonus en vigueur à `date` ('YYYY-MM-DD' ou 'YYYY-MM').
// SANS date → palier BASELINE (le premier = 01/04/2026), celui contre lequel les fiches
// Liantis et la suite de tests sont validées. La PRODUCTION (générateur de fiche / clôture)
// passe TOUJOURS la période de la fiche → le bon palier s'applique (ex. +2 % au 01/07/2026).
// Ce défaut « baseline » garantit que tout appelant sans date garde le comportement VALIDÉ
// (pas de dérive silencieuse d'une fixture certifiée). Barème daté : lois-belges.js
// (werkbonusSocialParPalier). Une date antérieure au 1er palier → plus ancien palier connu.
function werkbonusPalier(date) {
  const paliers = LOIS_BELGES.pp.werkbonusSocialParPalier;
  if (!Array.isArray(paliers) || !paliers.length) return LOIS_BELGES.pp.werkbonusSocial || {};
  const d = date ? String(date).slice(0, 10) : null;
  // ⚠️ SANS DATE → la BASELINE, jamais `paliers[0]` (corrigé le 26/08/2026).
  // Tant que la table commençait au 01/04/2026, les deux revenaient au même.
  // Depuis qu'elle porte les paliers de janvier et de mars 2026, `paliers[0]`
  // rendrait le barème de JANVIER à tout appel sans période — c'est-à-dire aux
  // simulateurs, aux garde-fous et aux 30+ fixtures Liantis certifiées, qui
  // sont toutes validées contre le palier d'avril. L'alias `werkbonusSocial`
  // EST cette baseline, et c'est ce que son commentaire annonce déjà dans
  // lois-belges.js. Un ajout de palier antérieur ne doit jamais déplacer la
  // référence des fiches certifiées.
  if (!d) return LOIS_BELGES.pp.werkbonusSocial || paliers[0];
  let sel = paliers[0];
  for (const p of paliers) { if (p.from <= d) sel = p; else break; }
  return sel;
}

function werkbonusVolets(S, isOuvrier, date) {
  const cfg = (werkbonusPalier(date) || {})[isOuvrier ? 'ouvrier' : 'employe'];
  if (!cfg) return { A: 0, B: 0 };
  const vol = (v) => {
    if (S <= v.s1) return v.max;
    if (S <= v.s2) return Math.max(0, v.max - v.pente * (S - v.s1));
    return 0;
  };
  return { A: _r2(vol(cfg.A)), B: _r2(vol(cfg.B)) };
}

// Bonus à l'emploi SOCIAL octroyé (après écrêtement au plafond ONSS dû — volet B puis volet A).
// Retourne { voletA, voletB, total } — total = réduction ONSS personnelle effective.
// Source: Instructions administratives ONSS 2026 (barèmes 01/04/2026).
export function calcWerkbonusDetail(brutMensuel, isOuvrier = false, mu = 1, date) {
  if (!brutMensuel || brutMensuel <= 0) return { voletA: 0, voletB: 0, total: 0 };
  // ── Temps partiel / prestations incomplètes (Instructions administratives ONSS) ──
  // μ = fraction d'occupation (heures prestées / heures temps plein), arrondie à 2
  // décimales et plafonnée à 1. On lit le bonus de base R sur le salaire ramené au
  // temps plein (S = brut / μ), PUIS on proratise (P = μ × R). μ = 1 (défaut) →
  // temps plein, comportement strictement inchangé (rétro-compatible).
  const m = (typeof mu === 'number' && mu > 0) ? Math.min(1, Math.round(mu * 100) / 100) : 1;
  const Sref = m < 1 ? _r2(brutMensuel / m) : brutMensuel;
  const baseONSS = isOuvrier ? brutMensuel * TX_OUV108 : brutMensuel;
  const onssDu = _r2(baseONSS * TX_ONSS_W);
  let { A, B } = werkbonusVolets(Sref, isOuvrier, date);
  if (m < 1) { A = _r2(A * m); B = _r2(B * m); }   // proratisation P = μ × R
  let total = _r2(A + B);
  if (total > onssDu) {                       // écrêtement : volet B d'abord, puis volet A
    const excedent = _r2(total - onssDu);
    const reduB = Math.min(B, excedent);
    B = _r2(B - reduB);
    A = _r2(Math.max(0, A - (excedent - reduB)));
    total = _r2(A + B);
  }
  return { voletA: A, voletB: B, total };
}

// Werkbonus / Bonus social — réduction ONSS personnelle (number, rétro-compatible).
export function calcWerkbonus(brutMensuel, isOuvrier = false, mu = 1, date) {
  return calcWerkbonusDetail(brutMensuel, isOuvrier, mu, date).total;
}

// Bonus à l'emploi FISCAL — réduction du précompte professionnel (number).
// = pctA × volet A social octroyé + pctB × volet B social octroyé, plafonné à maxAnnuel/12.
// Source : Annexe III AR/CIR 92 n° 46.2 (volet A 33,14 %).
// 🔴 Le volet B est DATÉ depuis le 20/08/2026 : 52,54 % jusqu'au 31/07/2026, puis
// 63 % à partir du 01/08/2026 — AR du 24/07/2026, MB 30/07/2026 éd. 2, art. 4 et 7.
// On lit donc le palier de la DATE DE LA FICHE, jamais un pourcentage à plat :
// sinon août sert du périmé (précompte trop élevé, net trop bas) et une refonte de
// fiche de juillet réécrirait le passé. Sans date → BASELINE, comme werkbonusPalier().
export function calcBonusEmploi(brutMensuel, isOuvrier = false, mu = 1, date) {
  if (!brutMensuel || brutMensuel <= 0) return 0;
  const F = getBonusEmploiFiscal(date);
  if (!F) return 0;
  const wb = calcWerkbonusDetail(brutMensuel, isOuvrier, mu, date);
  let fiscal = wb.voletA * (F.pctA || 0) + wb.voletB * (F.pctB || 0);
  const capMensuel = (F.maxAnnuel || 0) / 12;
  if (capMensuel > 0) fiscal = Math.min(fiscal, capMensuel);
  return _r2(Math.max(0, fiscal));
}

// ── Détail bonus emploi (social volet A + volet B octroyés) ──
export function calcBonusEmploiDetail(brutMensuel, isOuvrier = false, mu = 1, date) {
  const wb = calcWerkbonusDetail(brutMensuel, isOuvrier, mu, date);
  return { voletA: wb.voletA, voletB: wb.voletB, total: wb.total };
}

// ═══ QUICK PP/NET — Wrappers vers calcPrecompteExact (CORRECT) ═══
export function quickPP(brut,sit,enf){return calcPrecompteExact(brut,{situation:sit||'isole',enfants:enf||0}).pp;}
export function quickNet(brut,sit,enf){const o=Math.round(brut*TX_ONSS_W*100)/100;return Math.round((brut-o-quickPP(brut,sit,enf))*100)/100;}

// ═══ CALCUL PAYROLL COMPLET — Wrapper unifie sur calcPrecompteExact + calcCSSS + calcBonusEmploi ═══
// FIX AUDIT 21/03/2026: l'ancienne version avait des tranches PP fausses (32,10%),
// QE obsolete (880,83), bonus emploi faux (308,33), CSSS sur brut mensuel.
// Tout passe maintenant par les fonctions correctes qui lisent LOIS_BELGES.
export function calcPayroll(brut,statut,familial,charges,regime,opts={}){
  if(!brut||brut<=0)return{brut:0,onssP:0,imposable:0,pp:0,csss:0,bonusEmploi:0,net:0,onssE:0,coutTotal:0,details:{}};
  const r=(regime||100)/100;
  const brutR=Math.round(brut*r*100)/100;
  // ── ATN (avantages en nature) — optionnels, défaut 0 → calcul IDENTIQUE (parité préservée).
  //    Voiture : imposable (PP) mais EXEMPTÉE d'ONSS travailleur (cotisation solidarité CO2 =
  //    employeur, traitée à part). GSM/PC/logement (atnAutre) : imposable + ONSS travailleur.
  //    L'ATN n'est pas du cash → il augmente la base taxable mais n'est jamais ajouté au net.
  const atnVoiture = Math.max(0, +(opts.atnVoiture) || 0);
  const atnAutre   = Math.max(0, +(opts.atnAutre) || 0);
  const atnImpo    = Math.round((atnVoiture + atnAutre) * 100) / 100;
  const atnOnss    = Math.round(atnAutre * 100) / 100;
  // Map familial string to situation for calcPrecompteExact
  let situation='isole';
  if(familial==='marie_1rev'||familial==='marie_1r')situation='marie_1r';
  else if(familial==='marie_2rev'||familial==='marie_2r')situation='marie_2r';
  else if(familial==='cohabitant')situation='cohabitant';
  // Un DIRIGEANT / indépendant ne subit AUCUNE retenue de sécurité sociale salariée
  // (ONSS travailleur, CSSS, bonus emploi, ONSS patronal) — il cotise INASTI à part.
  // Gate `isDirigeant` : chaque retenue salariée → 0, le chemin salarié reste
  // STRICTEMENT inchangé. CORR 2026-07-20 (classe dirigeant, cf. calcPrecompteExact).
  const isDirigeant = statut === 'dirigeant' || statut === 'independant';
  // ── Cotisation sociale INASTI PRISE EN CHARGE par la société POUR le dirigeant ──
  //   Quand la société paie la cotisation sociale personnelle (INASTI) du dirigeant ET
  //   l'attribue au moins une fois par mois, c'est une rémunération PÉRIODIQUE imposable
  //   (avantage) → elle entre dans la base du PRÉCOMPTE MENSUEL, exactement comme un ATN
  //   non-cash : imposable, jamais versée en cash, le surcroît d'impôt réduit le net.
  //   Source : AR/CIR 92, Annexe III, n°17 (« rémunérations périodiques = revenus payés
  //   ou attribués régulièrement et AU MOINS UNE FOIS PAR MOIS ») ; n°18 (non périodique =
  //   le reste, calcul séparé). Vérifié 2026-07-26 contre fiche Liantis d'un dirigeant
  //   (rém 4 942,33 + INASTI 319,12 → imposable 5 261,45 → PP 1 292,33). Réservé au
  //   dirigeant ; défaut 0 → chemin salarié ET dirigeant sans prise en charge STRICTEMENT
  //   inchangés (validations 29/29 Liantis intactes).
  const cotisInastiDirigeant = isDirigeant ? Math.max(0, +(opts.cotisInastiPriseEnCharge) || 0) : 0;
  const onssP=isDirigeant?0:Math.round(brutR*TX_ONSS_W*100)/100;
  const isOuvrier = statut === 'ouvrier';
  // Période de la fiche → sélectionne le PALIER de barème werkbonus (indexations datées).
  // ⚠️ Défaut (absent) = palier BASELINE, c.-à-d. le PLUS ANCIEN (paliers[0]) — voir
  // werkbonusPalier() plus haut. Ce défaut est VOULU (il fige le comportement validé), mais
  // il n'est PAS le barème courant : tout appelant qui veut le barème du mois DOIT passer
  // { date }. Ce commentaire disait l'inverse et a causé un vrai bug dans avis-paiements-core.
  const ficheDate = opts.date || opts.periode || null;
  // ── Bonus à l'emploi SOCIAL (volets A+B) — réduction ONSS personnelle, plafonnée à l'ONSS dû ──
  const werkbonus = isDirigeant ? 0 : calcWerkbonus(brutR, isOuvrier, r, ficheDate);
  const onssNetW = Math.round(Math.max(0, onssP - werkbonus) * 100) / 100;
  // ── Précompte : base imposable sur l'ONSS APRÈS werkbonus social (méthode off. 2026) ──
  const ppResult=calcPrecompteExact(brutR,{situation,enfants:charges||0,dirigeant:isDirigeant,regime:100,werkbonusSocial:werkbonus});
  const pp=ppResult.pp;
  const imposable=Math.round((brutR-onssNetW)*100)/100;
  // CSSS : la TRANCHE se détermine sur la rémunération TRIMESTRIELLE. Défaut moteur
  // (calcCSSS) = brut×3, qui SUPPOSE 3 mois pleins → sur-retenue pour une entrée/sortie
  // en cours de trimestre. L'appelant qui connaît le trimestre réel (générateur de fiche :
  // Σ des mois du trimestre × prorata calendaire) le passe via opts.brutTrimestrielReel.
  // Absent → comportement historique strictement préservé (rétro-compatible, 29/29 intacts).
  const _brutTrimCSSS = (opts.brutTrimestrielReel != null && Number.isFinite(Number(opts.brutTrimestrielReel)))
    ? Number(opts.brutTrimestrielReel) : undefined;
  const csss=isDirigeant?0:calcCSSS(brutR,situation,statut==='ouvrier',_brutTrimCSSS);
  // ── Ouvrier: base ONSS = brut × 108% (déclaré à 108%) ──
  // ── Bonus à l'emploi FISCAL — réduit le précompte professionnel ──
  // = 33,14% volet A + 52,54% volet B du bonus social octroyé, plafonné (≠ forfait plat)
  const bonusEmploiFiscal = isDirigeant ? 0 : calcBonusEmploi(brutR, isOuvrier, r, ficheDate);
  const ppFinal = Math.round(Math.max(0, pp - bonusEmploiFiscal) * 100) / 100;
  const base108 = isOuvrier ? Math.round(brutR * TX_OUV108 * 100) / 100 : brutR;
  const onssBase = isOuvrier ? Math.round(base108 * TX_ONSS_W * 100) / 100 : onssP;
  //   Base imposable AFFICHÉE : + cotisation INASTI prise en charge (rémunération périodique
  //   imposable, dirigeant) — défaut 0 → inchangé (AR/CIR 92 Annexe III n°17, voir plus haut).
  const imposableReel = Math.round((brutR - onssBase + cotisInastiDirigeant) * 100) / 100;
  // ── Effet INCRÉMENTAL de l'ATN (isolé pour ne pas toucher le calcul salaire validé) ──
  let atnDeltaPP = 0, atnDeltaOnss = 0;
  if (atnImpo > 0) {
    // 🔴 CORRIGÉ 09/09/2026 (audit moteur) : ce calcul ne passait pas `werkbonusSocial`,
    // contrairement au `pp` de référence juste au-dessus (ligne ~480). Sans lui,
    // `calcPrecompteExact` retombait sur son fallback interne (ligne ~130), qui recalcule
    // SON PROPRE werkbonus sur le barème EMPLOYÉ (isOuvrier absent → false) et à temps
    // plein (mu=1, régime réel jamais transmis ici) — jamais celui de l'ouvrier/temps
    // partiel réel. Les DEUX termes de la soustraction `ppAvecAtn - pp` doivent partager
    // le MÊME werkbonus (même principe que la correction heures sup, PR #632/#633,
    // fiche-paie-generator.js:1375) : on réutilise ici la valeur déjà calculée `werkbonus`
    // (ligne ~477), plutôt que de laisser `calcPrecompteExact` en recalculer une fausse.
    // Impact vérifié le 09/09/2026 : 0 fiche réelle touchée à ce jour (aucun ouvrier ni
    // temps partiel du portefeuille actuel ne porte d'avantage en nature) — correctif
    // préventif avant qu'un vrai cas ne se présente.
    const ppAvecAtn = calcPrecompteExact(brutR + atnImpo, {situation,enfants:charges||0,dirigeant:isDirigeant,regime:100,werkbonusSocial:werkbonus}).pp;
    atnDeltaPP   = Math.round(Math.max(0, ppAvecAtn - pp) * 100) / 100; // PP additionnel dû à l'ATN
    atnDeltaOnss = isDirigeant ? 0 : Math.round(atnOnss * TX_ONSS_W * 100) / 100; // ONSS travailleur sur GSM/PC/logement (aucun pour dirigeant)
  }
  // ── Effet INCRÉMENTAL de la cotisation INASTI prise en charge (dirigeant) — même logique
  //    isolée que l'ATN : n'ajoute QUE le précompte dû sur la base supplémentaire périodique.
  let cotisInastiDeltaPP = 0;
  if (cotisInastiDirigeant > 0) {
    const baseAvant = brutR + atnImpo;
    const ppAvant = atnImpo > 0
      ? calcPrecompteExact(baseAvant, {situation,enfants:charges||0,dirigeant:isDirigeant,regime:100}).pp
      : pp;
    const ppApres = calcPrecompteExact(baseAvant + cotisInastiDirigeant, {situation,enfants:charges||0,dirigeant:isDirigeant,regime:100}).pp;
    cotisInastiDeltaPP = Math.round(Math.max(0, ppApres - ppAvant) * 100) / 100;
  }
  // Précompte AFFICHÉ = base salariale/dirigeant + surcroît dû à la cotisation INASTI périodique
  // (l'ATN, lui, reste déduit séparément du net — comportement historique inchangé).
  const ppFinalAffiche = Math.round((ppFinal + cotisInastiDeltaPP) * 100) / 100;
  // ── Net final (− retenues calculées sur l'ATN ; l'ATN lui-même n'est pas versé en cash) ──
  const net = Math.round((brutR - onssNetW - (isOuvrier ? Math.round((onssBase - onssP) * 100) / 100 : 0) - ppFinal - csss - atnDeltaPP - atnDeltaOnss - cotisInastiDeltaPP) * 100) / 100;
  // Cout employeur — aligné sur 25.07% (sans modération salariale séparée)
  // Dirigeant : pas d'ONSS patronal → coût employeur = brut (rémunération dirigeant).
  // 🔴 L'ATN ENTRE AUSSI DANS LA BASE PATRONALE (31/08/2026, demande explicite
  //    du fondateur — zone protégée K3, donc jamais touché sans son feu vert).
  //
  // Cette ligne calculait l'ONSS patronal sur le SEUL brut. Le côté travailleur
  // avait bien son terme d'ATN (`atnDeltaOnss`, l. ~462) ; l'employeur n'en avait
  // aucun. Or l'ONSS ne distingue pas les deux côtés : « les avantages en nature
  // sont considérés comme de la rémunération POUR LE CALCUL DES COTISATIONS de
  // sécurité sociale » — Instructions administratives 2026/3, « La notion de
  // rémunération > Avantages en nature », lu le 31/08/2026 :
  // https://www.socialsecurity.be/employer/instructions/dmfa/fr/latest/instructions/salary/particularcases/advantages_in_kind.html
  //
  // 📏 Mesure : 4 travailleurs du portefeuille portent un ATN GSM à 12 €/mois.
  //    12,00 × 25,07 % = 3,01 € de cotisation patronale non déclarée par mois et
  //    par travailleur, depuis leur entrée. Le montant est petit, la DmfA est
  //    fausse — et c'est la DmfA qui fait foi en cas de contrôle.
  //
  // ⚠️ `atnOnss`, PAS `atnImpo` : la VOITURE est imposable mais exclue de la
  //    cotisation ordinaire (elle relève de la cotisation de solidarité CO2, qui
  //    se déclare à part et n'est pas un pourcentage du brut). L'inclure ici la
  //    ferait payer DEUX FOIS. C'est la même distinction que côté travailleur.
  //
  // ⚠️ Le facteur 108 % de l'ouvrier ne s'applique QU'AU BRUT — l'avantage
  //    s'ajoute après, comme côté travailleur.
  //
  // ✅ PARITÉ : sans ATN, `atnOnss` vaut 0 et cette ligne rend exactement ce
  //    qu'elle rendait. Les 29/29 fiches Liantis n'en portent aucun.
  const onssE = isDirigeant ? 0 : Math.round((brutR + atnOnss) * TX_ONSS_E * 100) / 100;
  // ⚠️ `coutTotal` = brut + ONSS patronal, RIEN D'AUTRE. C'est le coût salarial
  //    de BASE, pas le coût employeur complet : il ignore l'accident du travail,
  //    les fonds sectoriels, la médecine du travail et — surtout — la cotisation
  //    vacances annuelles ONVA (15,84 %) qui ne frappe QUE les ouvriers.
  //    Conséquence mesurée : calcPayroll(2500,'ouvrier').coutTotal ===
  //    calcPayroll(2500,'employe').coutTotal. Le moteur ne distinguait pas les
  //    deux, alors qu'un ouvrier coûte 512,77 €/mois de plus à 2 500 € de brut
  //    (817,92 € à 4 000 €). Champ CONSERVÉ tel quel : 79 consommateurs et des
  //    tests certifiés l'épinglent — le changer d'autorité serait une régression
  //    silencieuse pour tous. Les appelants qui affichent un « coût employeur »
  //    doivent lire `coutEmployeurComplet` ci-dessous.
  const coutTotal = Math.round((brutR + onssE) * 100) / 100;
  // Coût employeur COMPLET — délégué à la source de vérité unique (l.424) :
  // ONSS patronal + AT + fonds (FFE, maladies pro) + médecine du travail
  // + ONVA 15,84 % pour l'ouvrier, moins la réduction premier engagement.
  // Ajouté le 2026-07-26. Additif : aucun champ existant n'est modifié.
  const coutEmployeurComplet = isDirigeant
    ? coutTotal // dirigeant : pas d'ONSS patronal, le coût EST la rémunération
    // `atnOnss` transmis pour la même raison qu'au-dessus : le coût employeur
    // COMPLET (ONSS + AT + fonds + ONVA ouvrier) se calcule sur la rémunération,
    // avantages en nature compris. Défaut 0 → coût strictement inchangé.
    : Math.round(calcCoutEmployeurFromBrut(brutR, { isOuvrier, atnOnss, ...(opts.coutOpts || {}) }) * 100) / 100;
  return {
    brut: brutR, onssP: onssBase, onssNetW, imposable: imposableReel,
    pp: ppFinalAffiche, ppBrut: Math.round(pp * 100) / 100, csss, net, onssE, coutTotal,
    coutEmployeurComplet,
    // Cotisation INASTI prise en charge (dirigeant) : base périodique + surcroît de précompte
    // qu'elle génère (AR/CIR 92 Annexe III n°17). Défaut 0 hors dirigeant avec prise en charge.
    cotisInastiPriseEnCharge: cotisInastiDirigeant, cotisInastiPPSupp: cotisInastiDeltaPP,
    bonusEmploi: Math.round((werkbonus) * 100) / 100,
    werkbonus, bonusEmploiB: bonusEmploiFiscal,
    // Réductions PP par volet — pour affichage Prestataire E-style.
    // Volet A (PP): réservé pour amélioration future (réduction PP bas salaires).
    // Volet B (PP): réduction effectivement appliquée au ppBrut via bonus emploi fiscal.
    redPPvoletA: 0,
    redPPvoletB: Math.round(Math.min(pp, bonusEmploiFiscal) * 100) / 100,
    // ATN (avantages en nature) : montants + retenues supplémentaires induites (pour la fiche).
    atn: { voiture: atnVoiture, autre: atnAutre, imposable: atnImpo, ppSupp: atnDeltaPP, onssSupp: atnDeltaOnss },
    // Ouvrier
    isOuvrier, base108: isOuvrier ? base108 : null,
    baseImp: Math.round((ppResult.detail.baseAnnuelle || 0) / 12 * 100) / 100,
    details: {
      qe: Math.round((ppResult.detail.redQE || 0) / 12 * 100) / 100,
      ppBrut: Math.round(pp * 100) / 100,
      tauxPP: imposableReel > 0 ? Math.round(ppFinalAffiche / imposableReel * 10000) / 100 : 0,
      tauxNet: brutR > 0 ? Math.round(net / brutR * 10000) / 100 : 0,
      situation,
    }
  };
}

// ═══ COÛT EMPLOYEUR — SOURCE DE VÉRITÉ UNIQUE ════════════════════════════════
// Coût employeur mensuel complet, réductions groupe-cible "premier engagement" incluses.
// = brut + ONSS patronal net + AT (0,97%) + fonds (FFE 0,17% + maladies pro 1,02%)
//   + [OUVRIER: cotisation vacances annuelles ONVA 15,84%] + médecine du travail − aide à l'emploi
// Réduction G1 (premier engagement) : couvre la cotisation patronale de BASE (100% 1er,
// 50% 2e, 25% 3e), plafonnée au forfait trimestriel (3100€ jusqu'au 30/06/2026, 2000€ après ;
// pour un salaire dont l'ONSS trim < forfait, la base est couverte à 100%).
// ⚠️ OUVRIERS (isOuvrier) : base ONSS employeur portée à 108% ; + cotisation patronale
//    vacances annuelles 15,84% (ONVA, code DmfA 253) HORS gestion globale → JAMAIS réduite
//    par le 1er engagement. Source : Instructions ONSS 2026/2 (vérifié 2026-07-01).
// opts: { nrEngagement, allocTravail, isOuvrier }
/**
 * RÉDUCTION GROUPE-CIBLE « PREMIERS ENGAGEMENTS » — LA RÈGLE, EN UN SEUL ENDROIT.
 *
 * ⚠️ EXTRAITE LE 21/08/2026, ET C'EST LE POINT DU CORRECTIF. Cette règle vivait
 * ici pour le moteur batch, et une SECONDE fois — différente — dans
 * `fiche-paie-generator.js`. Le générateur exigeait en plus, pour l'accorder :
 *   · un drapeau `clients.data.premier_engagement_actif` hydraté ;
 *   · ou une date d'entrée AVANT le 01/01/2024.
 * Le premier est une donnée saisie à la main, donc souvent absente ; la seconde
 * encode une prémisse que le commentaire voisin du générateur qualifiait
 * lui-même de FAUSSE (la réduction du 1er travailleur n'a pas été supprimée au
 * 01/01/2024 — ce sont les rangs 4/5/6 dont le droit ne s'ouvre plus).
 * Conséquence : sur un dossier sans le drapeau, le PDF perdait toute la
 * réduction et surévaluait le coût employeur de 562,94 € par rapport à la base,
 * dette épinglée dans `parite-fiche-baseline.js` depuis le 14/08/2026.
 * Une règle légale unique ne peut pas avoir deux implémentations.
 *
 * ⚠️ CE QUI N'A PAS CHANGÉ : les taux, les rangs et le plafond sont repris à
 * l'identique. C'est une EXTRACTION, pas une nouvelle règle — les 29/29 fiches
 * Liantis restent la référence.
 *
 * Rangs 1 à 5 → la cotisation de base patronale, bornée au forfait du rang
 * (G21 = 2.000 €/trim au rang 1, G1 = 1.000 €/trim aux rangs 2 à 5).
 * AT, fonds sectoriels et vacances annuelles ouvriers en sont exclus.
 *
 * ⚠️ PLAFOND DU FORFAIT — il manquait autrefois, et il coûtait de l'argent dans
 * le mauvais sens. Depuis le 01/07/2026 (loi-programme du 30/05/2026,
 * instruction ONSS du 02/07/2026, texte primaire lu le 07/08/2026 →
 * LOIS_BELGES.onss.premierEngagement), la réduction n'est plus « toute la
 * cotisation de base » mais un FORFAIT TRIMESTRIEL : 2.000 € pour le 1er
 * travailleur, 1.000 € pour les rangs 2 à 5. Sans borne, on déduisait la
 * cotisation entière — sur une ouvrière CP 302 à 2.504,53 €, 678,02 €/mois au
 * lieu de 666,67 €, soit 34,06 € de déduction en trop par trimestre. La DmfA
 * porte cette déduction (bloc 3315) : l'excédent se fait régulariser par l'ONSS.
 * Imputation MENSUELLE (forfait ÷ 3), décision fondateur du 11/08/2026.
 * L'instruction ONSS raisonne par trimestre ; savoir si un mois faible peut
 * compenser un mois fort n'y est pas écrit — à trancher sur le texte avant de
 * passer à une imputation trimestrielle glissante. Le plafond mensuel est la
 * lecture prudente.
 *
 * @param {number} onssE  cotisation de base patronale du mois
 * @param {number} nrEngagement  rang du travailleur (1 à 5 ; 0 ou > 5 = pas de droit)
 * @returns {number} montant de la réduction, borné au forfait mensuel
 */
export function reductionPremierEngagement(onssE, nrEngagement, opts = {}) {
  const r2x = (n) => Math.round(n * 100) / 100;
  const base = +onssE || 0;
  const nr = +(nrEngagement || 0);
  if (base <= 0) return 0;
  // ⚠️ ÉLIGIBILITÉ — LE RANG NE SUFFIT PAS, ET ÇA A DÉJÀ COÛTÉ (21/08/2026).
  // La réduction s'apprécie au niveau de l'UNITÉ TECHNIQUE D'EXPLOITATION, pas
  // de la société. Les travailleurs TRANSFÉRÉS (CCT 32bis hors faillite) gardent
  // le lien avec l'entité d'origine : le repreneur n'est pas un « nouvel
  // employeur » et la réduction n'est PAS due (ONSS, instructions DmfA 2026/2,
  // lues le 05/07/2026). Un rang encodé à 1 ne dit rien de tout cela.
  // Incident réel : GeeksDen / Yasmina Noury, exonérée à tort — 2 401,02 €
  // sous-déclarés sur un trimestre, corrigé le 18/07/2026 (PR #1010) en rendant
  // l'octroi OPT-IN par client.
  // ➜ `eligible: false` (défaut) = on n'applique rien. Sous-déclarer se rattrape,
  //   sur-déclarer se rembourse AVEC INTÉRÊTS.
  // ⚠️ `eligible: undefined` garde l'ancien comportement (rang seul) pour ne pas
  //   changer en silence les appelants qui ne savent pas encore répondre — ils
  //   sont nommés dans `__tests__/premier-engagement-eligibilite.test.js`.
  if (opts.eligible === false) return 0;
  // ═══ UN SEUL FORFAIT DU RANG 1 AU RANG 5 — les % de l'ancien régime sautent ══
  // Jusqu'au 21/08/2026 : rang 2 = 50 % de la cotisation de base, rang 3 = 25 %,
  // rangs 4 et 5 = RIEN. Ces pourcentages viennent d'AVANT la réforme et
  // n'existent dans AUCUN texte en vigueur. Résultat mesuré sur un brut de
  // 3.000 € : le rang 3 rendait 188,03 €/mois quand le rang 4 en rend 333,33 —
  // un troisième travailleur valait moins qu'un quatrième, ce qui ne se défend
  // nulle part. Et les rangs 4 et 5 ne rendaient rien du tout : le coût employeur
  // valait EXACTEMENT le même montant avec et sans réduction, alors que
  // `premiers-engagements.js` ouvrait déjà le droit.
  //
  // ✅ CHAÎNE COMPLÈTE, textes primaires lus sur Justel le 2026-08-16 :
  //   • loi-programme du 30/05/2026, art. 104 : à l'art. 342 de la loi-programme
  //     du 24/12/2002, « le mot "trois" est remplacé par le mot "cinq" » ;
  //     art. 108 : en vigueur le 01/07/2026 ;
  //   • AR du 16/05/2003, art. 16, § 1er, RÉÉCRIT par l'AR du 30/06/2026
  //     (art. 2, en vigueur 01/07/2026) — l'arrêté que l'instruction ONSS du
  //     02/07 disait encore attendre, il est publié :
  //       1° rang 1 → G21, pendant toute la durée de l'occupation ;
  //       2° à 5° rangs 2, 3, 4 et 5 → **G1**, maximum 12 trimestres dans une
  //          période de 20. Un seul et même montant pour les quatre rangs.
  //   • loi-programme du 24/12/2002, art. 336, § 1er : « G1 est égal à 1 000
  //     euros », « G21 est égal à 2000 euros ».
  //
  // ⚠️ PAS DE POURCENTAGE, ET C'EST DÉLIBÉRÉ. L'AR ne connaît qu'un forfait G
  // borné par ce qui reste dû (« Pg ne peut jamais être supérieure à G ») —
  // c'est exactement `Math.min(base, forfait/3)` ci-dessous. Garder un 50 % ou
  // un 25 %, ou en inventer un « par symétrie » pour les rangs 4-5, serait
  // inventer un mécanisme : le gate R2 l'interdit.
  //
  // 🔒 LE RANG 1 NE BOUGE PAS : c'est le seul que couvrent les 29/29 fiches
  // Liantis, et il valait déjà 100 % de la base plafonnée à G21/3.
  if (nr < 1 || nr > 5) return 0;
  const _pe = LOIS_BELGES?.onss?.premierEngagement;
  // Rangs 4 et 5 : ces clés existaient dans lois-belges.js depuis le 07/08/2026
  // avec ZÉRO lecteur — elles en ont un maintenant.
  const _forfaitTrim = _pe?.[`travailleur${nr}`]?.forfaitTrimestriel;
  let rgc = base;
  if (_forfaitTrim > 0) rgc = Math.min(rgc, r2x(_forfaitTrim / 3));
  return rgc > 0 ? rgc : 0;
}

export function calcCoutEmployeurFromBrut(brut, opts = {}) {
  const b = +brut || 0;
  if (b <= 0) return 0;
  const r2x = (n) => Math.round(n * 100) / 100;
  // ── DIRIGEANT INDÉPENDANT : AUCUNE charge patronale ────────────────────────
  // Un dirigeant d'entreprise n'est pas soumis à l'ONSS : ni cotisation patronale
  // (25,07 %), ni accident du travail, ni fonds sectoriel, ni médecine du travail,
  // ni pécule ONVA. Le coût pour la société = le brut versé (une éventuelle INASTI
  // prise en charge est ajoutée par l'appelant, ce n'est pas une charge ONSS).
  // Gate placé AVANT tout calcul salarié → le chemin salarié (dirigeant=false)
  // reste STRICTEMENT inchangé (parité 29/29). Même correctif que
  // app/pages/employees.js et que la branche `isDirigeant` de calcPayroll (l.555),
  // jamais porté ici : les appelants DIRECTS de cette fonction (computeFiche du
  // batch, dashboard) gonflaient le coût d'un dirigeant de ~26 %.
  // Verrou : __tests__/cout-employeur-dirigeant.test.js.
  if (opts.dirigeant) return r2x(b);
  const isOuvrier = !!opts.isOuvrier;
  // Base ONSS employeur = 108% pour les ouvriers (comme la cotisation travailleur).
  // ⚠️ L'AVANTAGE EN NATURE ENTRE DANS LA BASE, PAS DANS LE CASH (31/08/2026).
  // `atnOnss` = part d'ATN soumise à la cotisation ordinaire (GSM/PC/internet/
  // logement ; la voiture en est exclue — cotisation de solidarité CO2 à part).
  // Il gonfle la BASE de toutes les cotisations assises sur la rémunération,
  // mais jamais le terme `b` du retour : ce n'est pas de l'argent décaissé, il
  // est déjà payé sous forme d'avantage. Le confondre gonflerait le coût de
  // l'avantage lui-même, en plus de ses cotisations.
  // ⚠️ Le 108 % de l'ouvrier ne s'applique qu'au brut — l'ATN s'ajoute après,
  // exactement comme côté travailleur (payroll-engine, `atnDeltaOnss`).
  // Défaut 0 → base strictement inchangée (parité).
  const _atnBase = Math.max(0, +opts.atnOnss || 0);
  const baseE = r2x((isOuvrier ? r2x(b * TX_OUV108) : b) + _atnBase);
  // ONSS patronal ~25,07% = cotisation de base GLOBALISÉE. Elle inclut DÉJÀ les
  // maladies professionnelles (1%) et les accidents du travail — cf. source officielle
  // ONSS (vault Sources SPF/onss-contributions.md l.2587/2651 + page live
  // socialsecurity.be/employer/instructions/dmfa, lue le 09/09/2026 : « The 0.30%
  // work accident contribution is included within the total percentage… not a
  // separate special contribution ») + table « Part patronale » qui liste
  // « Maladies professionnelles : 1,00 » et « Accidents du travail : 0,30 » COMME
  // COMPOSANTS. Preuve : pour les travailleurs qui n'y ont pas droit, l'ONSS RETIRE
  // ces deux taux de la base 23,07% → ils y sont.
  const onssE = r2x(baseE * TX_ONSS_E);
  // FFE = cotisation spéciale sectorielle CP200 (0,17%), HORS de la base globalisée
  // (les cotisations spéciales ne sont pas dans le % de base — même source, l.2868).
  // ⚠️ CORRECTIF 2026-07-18 (source officielle ONSS ci-dessus) : les maladies pro (1,02%)
  // étaient rajoutées ICI alors qu'elles sont DÉJÀ dans le 25,07% → DOUBLE-COMPTAGE de
  // ~1% du brut sur CHAQUE coût employeur (jamais validé, les 29/29 Liantis ne couvraient
  // que le NET). Retirées. Verrou : __tests__/cout-employeur-maladies-pro.test.js.
  // 🔴 CORRECTIF 2026-09-09 (même source, même mécanisme, pas corrigé la 1re fois) :
  // les accidents du travail (`TX_AT`, 0,97%) étaient rajoutés ICI EN PLUS — le
  // commentaire ci-dessus le disait déjà inclus dans le 25,07% depuis juillet, mais
  // le calcul continuait à l'ajouter quand même via `TX_AT`, la cotisation ONSS
  // (0,30% chez l'ONSS, mais recopiée ici à 0,97% sans source vérifiable — « AR
  // 10/04/2014 » ne concerne ni l'ONSS ni les accidents du travail, aucun AR à
  // cette date sur ce sujet trouvé sur Justel).
  // 🔴 CORRECTIF 2026-09-11 : retirer purement et simplement `at` a fait DISPARAÎTRE
  // une vraie ligne de coût — l'assurance-loi accidents du travail PRIVÉE
  // (Loi du 10/04/1971), obligatoire pour tout employeur belge, distincte de la
  // cotisation ONSS et jamais globalisée dans le 25,07%. `TX_AT` (0,97%) était
  // en réalité une approximation de CETTE prime, pas de la part ONSS (0,30%) —
  // sa proximité avec `TX_AT_ASSURANCE` (1%, LB.assurances.accidentTravail) le
  // confirme. `fiche-paie-generator.js` (l'autre moteur) continuait de la
  // facturer sans interruption : la retirer d'un seul côté a fait diverger les
  // deux moteurs de ~22 €/mois au lieu de la dette de 0,67 € déjà connue et
  // documentée (0,97% vs 1% — même prime, deux estimations). Unifié sur
  // `TX_AT_ASSURANCE`, la même constante que `doc-generators.js` (corrigée par
  // cette PR) et que `fiche-paie-generator.js` (déjà correct). Verrous :
  // __tests__/fiche-et-batch-meme-argent.test.js, __tests__/rgpt-batch-et-fiche-meme-chiffre.test.js.
  const at = r2x(baseE * TX_AT_ASSURANCE);
  const txFFE = (LOIS_BELGES?.onss?.cp200?.cotSpecialeFFE) || 0.0017;
  const fonds = r2x(baseE * txFFE);
  const medecin = r2x(50 / 12);
  // Cotisation patronale VACANCES ANNUELLES ouvriers (ONVA) : 15,84% sur base 108%.
  // HORS gestion globale → NON couverte par la réduction 1er engagement.
  const txVacOuv = (LOIS_BELGES?.remuneration?.peculeVacances?.ouvrierPatronal?.pct) || 0.1584;
  const vacances = isOuvrier ? r2x(baseE * txVacOuv) : 0;
  const rgc = reductionPremierEngagement(onssE, opts.nrEngagement, { eligible: opts.premierEngagementEligible });
  const onssENet = Math.max(0, r2x(onssE - rgc));
  return r2x(b + onssENet + at + fonds + medecin + vacances - (+opts.allocTravail || 0));
}

// ═══ COTISATIONS SECTORIELLES (FSE patronal par CP) ═══════════════════════════
// Calcul des cotisations patronales mensuelles incluant ONSS_E + Fonds Social
// sectoriel (FSE) pour les CPs avec fonds spécifique. Utilisé pour la facture
// mensuelle de cotisations (équivalent du décompte secrétariat social Prestataire E).
//
// Pour Horeca CP 302 ouvrier : ONSS_E sur base 108% du brut.
//
// Sources :
//  - LB.TAUX_SECTORIELS[cp].fondsSE.taux_patronal — défini par CP
//  - Loi 27/06/1969 + AR 28/11/1969 (cotisation ONSS patronale)
//  - Note : taux FSE = approximation indicative. Les variations sous-sectorielles
//    (302.00 vs 302.01, 124.01 vs 124.05, etc.) doivent être confirmées via le
//    profil ONSS employeur (réf. catégorie ONSS).
//
// Retour :
//   {
//     onssE,         // ONSS patronal (base 100% ou 108% si ouvrier)
//     fondsSE,       // Cotisation Fonds Sécurité Existence sectoriel
//     fondsSE_nom,   // Libellé du fonds (pour affichage facture)
//     total,         // ONSS_E + FSE
//     baseONSS,      // Base de calcul ONSS (brut ou brut×1.08)
//     isOuvrier,
//     cp,
//     note,
//   }
export function calcCotisationsSectorielles(brutMensuel, cp, statut, options = {}) {
  const brut = Math.round((Number(brutMensuel) || 0) * 100) / 100;
  if (brut <= 0) {
    return { onssE: 0, fondsSE: 0, fondsSE_nom: null, total: 0, totalHorsPension: 0, baseONSS: 0, isOuvrier: false, cp: cp || null, note: 'Brut nul', fondsSE_origine: 'fiche', fondsSE_categorieOnss: null, fondsSE_categorieRequise: false, fondsSE_categorieNonRattachee: false, fondsSE_ambigu: null, pensionSectorielle: 0, pensionSectorielle_taux: 0, pensionSectorielle_prouvee: false, pensionSectorielle_raison: 'Brut nul' };
  }
  const isOuvrier = String(statut || '').toLowerCase().includes('ouvrier');
  const baseONSS  = isOuvrier ? Math.round(brut * TX_OUV108 * 100) / 100 : brut;
  const onssE     = Math.round(baseONSS * TX_ONSS_E * 100) / 100;

  // ═══════════════════════════════════════════════════════════════════════
  // LECTURE DU TAUX FSE — LA SOUS-CP D'ABORD, MAIS SEULEMENT SI ELLE EST PROUVÉE
  // ───────────────────────────────────────────────────────────────────────
  // AVANT LE 25/08/2026 : `String(cp).replace(/[^0-9]/g,'').slice(0, 3)`.
  // La troncature à trois chiffres rabattait TOUTE sous-CP sur sa mère. Le
  // registre porte pourtant un taux propre pour 26 des 74 sous-CP, et il
  // diffère de la mère. Conséquence chiffrée sur un brut de 2 500 €/mois :
  //   125.02 — 10,47 % propre contre 6 % lus → 111,75 € NON comptés
  //   125.03 — 10,28 % propre contre 6 % lus → 107,00 € NON comptés
  //   301.02 —  5,97 % propre contre 5 % lus →  24,25 € NON comptés
  //
  // ⛔ MAIS LA LEVÉE N'EST PAS INCONDITIONNELLE, ET C'EST DÉLIBÉRÉ. Sur ces 26
  // sous-CP, la plupart des taux — des deux côtés — sont des nombres ronds sans
  // source. Lire la sous-CP les rendrait plus SPÉCIFIQUES sans les rendre plus
  // JUSTES, et pour plusieurs cela ferait BAISSER la cotisation facturée : un
  // sous-comptage est plus grave qu'un surcoût, le client le découvre après.
  //
  // La règle retenue : **on descend à la sous-CP quand son taux est PROUVÉ**,
  // c'est-à-dire porté par un bloc `cctSourcee` — une convention lue au texte
  // primaire, citée et datée. Sinon on garde le comportement historique.
  // Le jour où une sous-CP est sourcée, elle devient active sans toucher ici.
  // Verrou : __tests__/moteur-sous-cp-fonds.test.js
  // ⚠️ LA RÈGLE A DÉMÉNAGÉ LE 25/08/2026 — ELLE EST LA MÊME, ELLE N'EST PLUS ICI.
  // Elle vivait dans cette fonction, donc l'écran de la fiche CP en appliquait
  // une autre et les deux ne donnaient pas le même montant sur 24 couples
  // CP × public. Elle est désormais dans `fonds-se-cotisation.js`, avec son
  // « pourquoi », et les deux surfaces l'appellent. R2 ter-C : une cause se
  // répare sur toutes ses surfaces, en une seule fois.
  const cpBrut = String(cp || '').trim();
  const cpMere = cpBrut.replace(/[^0-9]/g, '').slice(0, 3);
  const { bloc: fse, cle: cpKey } = blocFondsSE(cpBrut || cpMere, TAUX_SECTORIELS);
  // ── TAUX PAR PUBLIC ET BASE 108 %, UNIQUEMENT SUR LES TAUX SOURCÉS ONSS ──────
  // Même garde que la levée de troncature ci-dessus, et pour la même raison : la
  // règle est juste, mais l'appliquer aux taux ronds non sourcés les rendrait
  // différents sans les rendre plus justes.
  //
  // Deux corrections que seul un taux ONSS permet, parce que lui seul dit à quel
  // public et à quelle base il se rapporte :
  //   · le taux ouvrier et le taux employé peuvent tout opposer — CP 126 :
  //     15,45 % contre 0,23 %, les employés relevant du Fonds social CP 200 ;
  //   · le code 820 est dû « sur les rémunérations à 108 % des travailleurs
  //     manuels » (intitulé ONSS, annexe 2). L'appliquer au brut nu sous-compte
  //     de 8 % — et un sous-comptage se découvre après, chez le client.
  //
  // Sans le drapeau `onssSourcee`, rien ne change : même taux, même base qu'avant.
  // La règle vit dans `fonds-se-cotisation.js` — un seul endroit la calcule,
  // pour les 19 moteurs sectoriels et les 12 écrans comme pour ici.
  const fseNom   = fse?.nom || null;
  const fseNote  = fse?.note || null;
  // ── LA CATÉGORIE D'EMPLOYEUR, QUAND LE DOSSIER LA PORTE (25/08/2026) ────────
  // Dix-neuf des 95 CP couvertes par l'ONSS ont un taux qui DÉPEND de la
  // catégorie d'employeur — jusqu'à un facteur 125 (CP 145 horticulture :
  // 0,10 % contre 12,53 %). Sans elle, ces CP portaient un nombre rond jamais
  // sourcé, choisi faute de mieux. Avec elle, c'est le percepteur qui répond.
  // Le calcul reste dans `fonds-se-cotisation.js` : un seul endroit, comme pour
  // le public et la base 108 %. Ici on ne fait que lui passer la donnée.
  // Paramètre OPTIONNEL : sans lui, le résultat est identique à avant, et le
  // drapeau `fondsSE_categorieRequise` dit à l'écran de réclamer le champ.
  const optionsFse = { ouvrier: isOuvrier, cp: cpBrut || cpMere, categorieOnss: options.categorieOnss ?? null, estDossierAureus: Boolean(options.estDossierAureus) };
  const detailFse = tauxFondsSE(fse, optionsFse);
  const fondsSE  = cotisationFondsSE(fse, brut, optionsFse);
  // ── LE SECOND PILIER SECTORIEL (codes ONSS 825/835) ─────────────────────────
  // Ajouté le 25/08/2026. L'ONSS le perçoit sur 42 couples CP × public, de
  // 0,48 % à 4,95 %, et il n'entrait dans AUCUN coût affiché. Il vit dans son
  // PROPRE champ, jamais dans `fondsSE` : un second pilier n'est pas un fonds
  // de sécurité d'existence, et `fondsSE` s'affiche partout sous cette
  // étiquette. Le gate anti-double-comptage est en tête de
  // `pensionSectorielleOnss`.
  const detailPension = pensionSectorielleOnss(fse, optionsFse);
  const pensionSectorielle = cotisationPensionSectorielle(fse, brut, optionsFse);

  return {
    onssE,
    fondsSE,
    fondsSE_nom: fseNom,
    // ⚠️ `total` COMPTE DÉSORMAIS LE SECOND PILIER — changement du 25/08/2026.
    // Il valait `onssE + fondsSE` et servait de coût patronal sectoriel : en
    // laisser dehors une cotisation que l'ONSS perçoit, c'était sous-compter.
    // `totalHorsPension` garde l'ancienne somme pour qui a besoin des deux.
    total: Math.round((onssE + fondsSE + pensionSectorielle) * 100) / 100,
    totalHorsPension: Math.round((onssE + fondsSE) * 100) / 100,
    pensionSectorielle,
    pensionSectorielle_taux: detailPension.taux,
    // Pourquoi elle vaut 0 quand elle vaut 0 : « le taux du fonds n'est pas
    // sourcé » n'est pas la même chose que « ce secteur n'en a pas ».
    pensionSectorielle_prouvee: detailPension.prouve,
    pensionSectorielle_raison: detailPension.raison,
    baseONSS,
    isOuvrier,
    cp: cpKey || null,
    note: fseNote,
    // D'où vient le taux, et ce qui manque pour qu'il soit certain. Ces champs
    // ne changent aucun montant : ils permettent à l'écran de dire « à
    // compléter » au lieu de présenter un pari comme un chiffre.
    fondsSE_origine: detailFse.origineTaux,
    fondsSE_categorieOnss: detailFse.categorieOnss,
    fondsSE_categorieRequise: Boolean(detailFse.categorieRequise),
    fondsSE_categorieNonRattachee: Boolean(detailFse.categorieNonRattachee),
    fondsSE_ambigu: detailFse.ambigu || null,
  };
}

// ═══ TARIF EXCEPTIONNEL (bijzonder tarief) — PP pecule double / 13e mois / primes ═══
// Source: SPF Finances — Annexe III AR/CIR 92, baremes 2026 des remunerations exceptionnelles.
// Methode OFFICIELLE : la tranche se choisit sur la remuneration annuelle BRUTE normale
// (brut mensuel x 12) ; le taux s'applique sur l'allocation diminuee de l'ONSS personnel.
// type = 'allocations' (primes, 13e mois, gratifications) | 'pecule' (pecule de vacances double).
function calcTarifExceptionnel(brutMensuelNormal, montantExceptionnel, type = 'allocations') {
  const annuelBrutNormal = Math.round(brutMensuelNormal * 12 * 100) / 100;
  const tables = (LOIS_BELGES.pp && LOIS_BELGES.pp.remunerationsExceptionnelles) || {};
  const table = (type === 'pecule' ? tables.pecule : tables.allocations) || [];
  // Premiere tranche dont la borne haute couvre le brut annuel normal
  let taux = table.length ? table[table.length - 1].taux : 0;
  for (const b of table) { if (annuelBrutNormal <= b.max) { taux = b.taux; break; } }
  // PP sur montant exceptionnel = (montant - ONSS) x taux de la table (pas de QE)
  const onssExc = Math.round(montantExceptionnel * TX_ONSS_W * 100) / 100;
  const imposableExc = Math.round((montantExceptionnel - onssExc) * 100) / 100;
  const pp = Math.round(imposableExc * taux * 100) / 100;
  return { pp, taux, imposable: imposableExc, onss: onssExc };
}
export { calcTarifExceptionnel };

// ═══ PRIME ENCODÉE (table payroll_primes) — drapeaux soumis_onss / soumis_pp ═══
// Calcule l'impact net d'une prime/indemnité encodée, avec assujettissement ONSS
// et précompte INDÉPENDANTS (deux booléens en base).
// Sources officielles (R2) :
//  • ONSS — cotisation personnelle travailleur = 13,07% du brut ; les frais propres
//    à l'employeur ne sont PAS de la rémunération → exonérés (cas soumis_onss=false).
//    socialsecurity.be/.../which-contributions.htm + .../particularcases/expensesreimbursement.html
//  • SPF Finances — le précompte est dû sur le brut DIMINUÉ des retenues sociales
//    (imposable = montant − ONSS) ; allocations exceptionnelles = barème Annexe III
//    (même table que calcTarifExceptionnel). finances.belgium.be/.../precompte_professionnel/calcul
// Le cas (soumis_onss=true && soumis_pp=true) reproduit EXACTEMENT
// calcTarifExceptionnel(brut, montant, 'allocations') — vérifié par test.
function calcPrime({ brutMensuelNormal = 0, montant = 0, soumisOnss = true, soumisPp = true } = {}) {
  const m = Math.round((Number(montant) || 0) * 100) / 100;
  if (m <= 0) return { brut: 0, onss: 0, pp: 0, net: 0, imposable: 0, taux: 0 };
  const onss = soumisOnss ? Math.round(m * TX_ONSS_W * 100) / 100 : 0;
  const imposable = Math.round((m - onss) * 100) / 100;
  let taux = 0;
  if (soumisPp) {
    const annuel = Math.round((Number(brutMensuelNormal) || 0) * 12 * 100) / 100;
    const table = (LOIS_BELGES.pp && LOIS_BELGES.pp.remunerationsExceptionnelles
      && LOIS_BELGES.pp.remunerationsExceptionnelles.allocations) || [];
    taux = table.length ? table[table.length - 1].taux : 0;
    for (const b of table) { if (annuel <= b.max) { taux = b.taux; break; } }
  }
  const pp = soumisPp ? Math.round(imposable * taux * 100) / 100 : 0;
  const net = Math.round((m - onss - pp) * 100) / 100;
  return { brut: m, onss, pp, net, imposable, taux };
}
export { calcPrime };

// ═══ PECULE VACANCES DOUBLE — FIX: tarif exceptionnel au lieu de 23,15% fixe ═══
// Fraction du double pécule soumise à la retenue personnelle spéciale de 13,07 %.
// Les 7/92 restants — le « double pécule complémentaire », dû à partir du 3e jour
// de la 4e semaine — en sont EXONÉRÉS.
// Source : ONSS, instructions administratives — « Retenue double pécule vacances
// secteur privé » (AR 30/03/1967 art. 46, mod. AR 08/02/2023). Vérifié le
// 2026-07-27, recoupé Acerta (« 85/92e ») et Securex (« 85 % de ce pécule »).
const PECULE_DOUBLE_PART_SOUMISE = 85 / 92;

/**
 * Double pécule de vacances d'un EMPLOYÉ.
 *
 * ⚠️ TROIS CORRECTIONS le 2026-07-27, toutes sourcées ONSS (voir ci-dessous).
 * Le résultat précédent était faux dans les trois dimensions à la fois.
 *
 * 1. BASE — `brutAnnuel * 92 %` donnait un pécule DOUZE FOIS trop élevé.
 *    Le registre est explicite : `peculeVacances.double` porte `base: 'brut
 *    mensuel'` (le pourcentage vit dans lois-belges.js, jamais ici — R1).
 *    Un employé à 2 458 €/mois a droit à 2 261,36 €,
 *    pas à 27 136,32 €. Le seul appelant (avis-paiements-core:157) passait bien
 *    un brut ANNUEL, et injectait ensuite base/onss/pp dans les totaux
 *    trimestriels ONSS et précompte des avis de paiement.
 *    À noter : `regression-payroll-2026.test.js` figeait la valeur annuelle
 *    (l.502) tout en affirmant ailleurs, dans [BUG-002] (l.574), que
 *    `brut mensuel × 0,92` était le bon calcul. Le fichier se contredisait.
 *
 * 2. ASSIETTE DE LA RETENUE — 13,07 % s'appliquait à 92/92 du pécule.
 *    L'ONSS exonère la part « à partir du 3e jour de la 4e semaine » : la
 *    retenue porte sur 85/92. Sur-retenue de ~274 € sur le cas de référence.
 *
 * 3. `cotSpec` — une « cotisation » de 0,97 % était déduite du NET du
 *    travailleur, en réutilisant `TX_AT`, documenté comme la cotisation ONSS
 *    ACCIDENTS DU TRAVAIL, donc PATRONALE. Aucune des cotisations spéciales
 *    listées par l'ONSS ne correspond à une retenue travailleur de 0,97 % sur
 *    le double pécule : elle n'existe pas. Le champ est conservé à 0 pour ne
 *    casser aucun appelant, et ne participe plus au net.
 *    (Piège écarté : la retenue de solidarité de 1 % existe bel et bien, mais
 *    pour les OUVRIERS, sur le pécule versé par l'ONVA — pas ici.)
 *
 * @param {number} brutAnnuel     rémunération annuelle brute (sert à choisir la
 *                                tranche du tarif exceptionnel).
 * @param {number} [brutMensuelRef] brut mensuel de référence ; à défaut,
 *                                  déduit de l'annuel.
 */
export function calcPeculeDouble(brutAnnuel, brutMensuelRef, opts){
  const brutMensuel = brutMensuelRef || (brutAnnuel / 12);
  const baseDu=Math.round(brutMensuel*PV_DOUBLE*100)/100;
  // ── 05/09/2026, ADDITIF (sans `opts` : comportement STRICTEMENT inchangé, validations 29/29) ──
  // AR 30/03/1967 art. 48 (rédaction issue de l'AR du 28/09/2023, texte primaire lu le
  // 04/09/2026 — corpus `ar1967-art48-imputation`) : le DOUBLE pécule de départ reçu de
  // l'employeur précédent (attestation de vacances) se déduit EN UNE FOIS, au moment où le
  // nouvel employeur paie le double pécule, et au plus à concurrence du double dû « si ces
  // prestations avaient été effectuées à son service ». `attestationRestant` porte ce que le
  // plafond n'a pas absorbé — il n'est pas perdu, il appartient à l'exercice suivant.
  // ⚠️ Ce paramètre n'est PAS encore alimenté automatiquement : l'attestation est saisie en
  // TOTAL (simple + double) sans ventilation PAR EXERCICE, or seul le double de l'exercice
  // d'entrée se déduit ici. Brancher sans cette ventilation couperait à tort le pécule du
  // travailleur — dit dans 00.02, à câbler quand le formulaire ventile par exercice.
  const dejaPaye = Math.max(0, +(opts && opts.doubleDejaPaye) || 0);
  const deduitAttestation = Math.min(baseDu, Math.round(dejaPaye*100)/100);
  const base=Math.round((baseDu-deduitAttestation)*100)/100;
  // Retenue personnelle spéciale 13,07 % — sur 85/92 du pécule uniquement.
  const onss=Math.round(base*PECULE_DOUBLE_PART_SOUMISE*TX_ONSS_W*100)/100;
  const cotSpec=0; // supprimée : sans base légale (voir §3 ci-dessus)
  const imposable=Math.round((base-onss)*100)/100;
  // Tarif exceptionnel « pécule de vacances » : la TRANCHE se lit sur la
  // rémunération annuelle normale, le TAUX s'applique au pécule diminué de la
  // retenue personnelle. On ne réutilise pas `te.pp` : la fonction re-déduit en
  // interne 13,07 % de la TOTALITÉ du montant, ce qui ignore l'exonération 7/92.
  const te = calcTarifExceptionnel(brutMensuel, base, 'pecule');
  const pp = Math.round(imposable * te.taux * 100) / 100;
  const net=Math.round((base-onss-pp)*100)/100;
  return{base,onss,cotSpec,imposable,pp,net,tauxExc:te.taux,
    baseDu, deduitAttestation, attestationRestant: Math.round((dejaPaye-deduitAttestation)*100)/100};
}

// ═══ PRORATA ═══
// Jours ouvrés (lun-ven) RÉELS d'un mois (mois = 1-12). Pour un employé mensuel, le
// diviseur de prorata = jours ouvrés réels du mois (variable 20/21/22/23), PAS un 22 fixe.
// Validé contre fiches Securex réelles : Rosero sept.2025 = 22 (4000×1/22 = 181,82) ;
// Inglese juin 2025 = 21 (3200×4/21 = 609,52). (Traitement des fériés dans le diviseur :
// à confirmer via une fiche d'un mois férié — voir registre/chantier.)
export function joursOuvresMois(annee, mois){
  const last = new Date(annee, mois, 0).getDate();
  let n = 0;
  for (let j = 1; j <= last; j++){
    const dow = new Date(annee, mois - 1, j).getDay();
    if (dow !== 0 && dow !== 6) n++;
  }
  return n;
}

// calcProrata : si opts.{annee,mois} fournis → diviseur = jours ouvrés RÉELS du mois
// (source de vérité unique, méthode Securex prouvée). Sinon fallback sur joursMois (ou 22).
export function calcProrata(brut, joursPreste, joursMois, opts){
  const jm = (opts && opts.annee && opts.mois)
    ? joursOuvresMois(opts.annee, opts.mois)
    : (joursMois || 22);
  const ratio = Math.min(joursPreste, jm) / jm;
  return Math.round(brut * ratio * 100) / 100;
}

// ═══ HEURES SUPPLÉMENTAIRES — sursalaire + dispense PP employeur + réduction d'impôt travailleur ═══
// Source : Loi 16/03/1971 art. 29 (sursalaire 50 % semaine/samedi, 100 % dimanche/férié,
//          20 % récupération) ; Art. 275/1 CIR92 (dispense employeur) + Art. 154bis CIR92
//          (réduction d'impôt travailleur). Quota RELEVÉ au 01/01/2026 par la loi du
//          15/07/2026 (MB 29/07/2026), art. 47-49 : 180 h en général, 280 h travaux
//          routiers/ferroviaires sous conditions, 360 h horeca. Les deux articles
//          portent les MÊMES nombres — un seul quota partagé est donc correct.
// Les DEUX avantages dépendent du taux de sursalaire (Circ. 2025/C/53) :
//   · sursalaire 20 %     → dispense employeur 32,19 % · réduction travailleur 66,81 %
//   · sursalaire 50/100 % → dispense employeur 41,25 % · réduction travailleur 57,75 %
// Constantes : LOIS_BELGES.tempsTravail.heuresSupp + LOIS_BELGES.dispensesPP.heuresSupp.
export function calcHeuresSupp(params = {}) {
  const r2x = (n) => Math.round(n * 100) / 100;
  const tauxHoraire = +(params.tauxHoraire || 0);
  const heures = +(params.heures || 0);
  const typeMaj = String(params.typeMajoration || '50'); // '20' | '50' | '100'
  const secteur = String(params.secteur || '').toLowerCase();
  const dejaFaites = Math.max(0, +(params.heuresAnneeDejaFaites || 0));

  const cfgT = (LOIS_BELGES.tempsTravail && LOIS_BELGES.tempsTravail.heuresSupp) || {};
  const cfgF = (LOIS_BELGES.dispensesPP && LOIS_BELGES.dispensesPP.heuresSupp) || {};

  if (tauxHoraire <= 0 || heures <= 0) {
    return { brut: 0, sursalaire: 0, remunerationBase: 0, dispensePP: 0, reductionImpotTravailleur: 0, heuresEligibles: 0, heuresHorsPlafond: 0, note: 'Heures ou taux nuls' };
  }

  const majoration = typeMaj === '100' ? (cfgT.majoration100 != null ? cfgT.majoration100 : 1.0)
                   : typeMaj === '20'  ? 0.20
                   : (cfgT.majoration50 != null ? cfgT.majoration50 : 0.5);
  const remunerationBase = r2x(tauxHoraire * heures);      // heures au taux normal
  const sursalaire = r2x(remunerationBase * majoration);   // la majoration seule
  const brut = r2x(remunerationBase + sursalaire);         // rémunération totale des heures sup

  // Plafond fiscal annuel (180 h en général ; 280 h routier/ferroviaire, 360 h horeca).
  // ⚠️ Le repli en dur valait 130 h — l'état d'AVANT la loi du 15/07/2026. Il ne sert
  // que si lois-belges.js est amputé, mais un repli périmé ment plus discrètement
  // qu'une clé absente : on l'aligne sur le régime général en vigueur.
  const plafond = (cfgF.plafondHeuresSecteurs && cfgF.plafondHeuresSecteurs[secteur]) || cfgF.plafondHeures || 180;
  const heuresEligibles = Math.max(0, Math.min(heures, plafond - dejaFaites));
  const heuresHorsPlafond = r2x(heures - heuresEligibles);
  const baseAvantage = r2x(tauxHoraire * heuresEligibles); // base = rém. brute au taux normal des heures éligibles

  // Taux dispense (employeur) + réduction d'impôt (travailleur) selon le sursalaire
  const is20 = typeMaj === '20';
  const tauxDispense = is20 ? (cfgF.dispenseEmployeur20 != null ? cfgF.dispenseEmployeur20 : 0.3219)
                            : (cfgF.dispenseEmployeur50_100 != null ? cfgF.dispenseEmployeur50_100 : 0.4125);
  const tauxReductionTrav = is20 ? (cfgF.reductionTravailleur20 != null ? cfgF.reductionTravailleur20 : 0.6681)
                                 : (cfgF.reductionTravailleur50_100 != null ? cfgF.reductionTravailleur50_100 : 0.5775);
  const dispensePP = r2x(baseAvantage * tauxDispense);                 // employeur : PP non versé
  const reductionImpotTravailleur = r2x(baseAvantage * tauxReductionTrav); // travailleur : réduction PP/impôt (Art. 154bis)

  return {
    tauxHoraire, heures, typeMajoration: typeMaj, majoration,
    remunerationBase, sursalaire, brut,
    plafondFiscal: plafond, heuresEligibles, heuresHorsPlafond,
    dispensePP, tauxDispense,
    reductionImpotTravailleur, tauxReductionTravailleur: tauxReductionTrav,
    quotaAnnuel: plafond,
    quotaSecteur: (cfgF.plafondHeuresSecteurs && cfgF.plafondHeuresSecteurs[secteur]) ? secteur : 'general',
    note: `Sursalaire ${Math.round(majoration * 100)}% — dispense employeur ${(tauxDispense * 100).toFixed(2)}% + réduction d'impôt travailleur ${(tauxReductionTrav * 100).toFixed(2)}% sur ${heuresEligibles}h · quota ${plafond}h/an (Art. 275/1 + 154bis CIR92)`,
  };
}

// ═══ 13EME MOIS — FIX: tarif exceptionnel au lieu de 23,15% fixe ═══
export function calc13eMois(brutMensuel){
  const brut=brutMensuel;
  const onss=Math.round(brut*TX_ONSS_W*100)/100;
  const imposable=Math.round((brut-onss)*100)/100;
  const te = calcTarifExceptionnel(brut, brut);
  const pp = te.pp;
  const net=Math.round((brut-onss-pp)*100)/100;
  return{brut,onss,imposable,pp,net,tauxExc:te.taux};
}

// ═══ QUOTITÉ SAISISSABLE (Art. 1409-1412 Code judiciaire) ═══
export function calcQuotiteSaisissable(netMensuel,nbEnfantsCharge=0,isRemplacement=false,isPensionAlim=false){
  if(isPensionAlim)return{saisissable:netMensuel,protege:0,tranches:[],enfantImmun:0,note:"Créance alimentaire: saisissable en totalité (art. 1412 CJ)"};
  const bareme=isRemplacement?SAISIE_2026_REMPLACEMENT:SAISIE_2026_TRAVAIL;
  let totalSaisissable=0;const tranches=[];
  for(const t of bareme){
    if(netMensuel<=t.min)break;
    const dansLaTranche=Math.min(netMensuel,t.max)-t.min;
    if(dansLaTranche<=0)continue;
    const retenue=+(dansLaTranche*t.pct/100).toFixed(2);
    tranches.push({min:t.min,max:Math.min(t.max,netMensuel),pct:t.pct,montantTranche:+dansLaTranche.toFixed(2),retenue,label:t.label});
    totalSaisissable+=retenue;
  }
  const enfantImmun=nbEnfantsCharge*SAISIE_IMMUN_ENFANT_2026;
  const saisissable=Math.max(0,+(totalSaisissable-enfantImmun).toFixed(2));
  const protege=+(netMensuel-saisissable).toFixed(2);
  return{saisissable,protege,tranches,enfantImmun,totalAvantImmun:+totalSaisissable.toFixed(2),note:null};
}

// ═══ ALLOCATIONS FAMILIALES ═══
export function calcAllocEnfant(region,birthYear,age){
  const reg=AF_REGIONS[region];if(!reg)return 0;
  const isNew=birthYear>=reg.cutoff;
  if(isNew){
    const tranche=reg.base.find(t=>age>=t.age&&age<=t.to);
    return tranche?tranche.amt:0;
  } else {
    if(region==='BXL'){
      const tranche=reg.base.find(t=>age>=t.age&&age<=t.to);
      return tranche?Math.max(tranche.amt-(reg.ancienReduction||0),0):0;
    }
    return reg.ancien?reg.ancien.rang1:0;
  }
}


// ═══════════════════════════════════════════════════════════
// CALCUL PAIE PAR STATUT — Tous les régimes belges
// ═══════════════════════════════════════════════════════════

export function calcPayrollStatut(statut, params = {}) {
  const {
    brut = 0,
    heures = 0,
    regime = 100,
    situationFamiliale = 'isole',
    enfants = 0,
    annee = new Date().getFullYear()
  } = params;

  const r2 = v => Math.round(v * 100) / 100;

// Affichage d'un taux en pourcentage belge (virgule) dans les notes de résultat.
// ⚠️ 20/08/2026 (descente de dette) : les notes recopiaient leurs taux à la
// main à côté de calculs qui les tiraient du registre — dérivées désormais.
const _pctNote = (t) => String(Math.round(t * 10000) / 100).replace('.', ',');
// Apprenti CCT n°1 : taux INDICATIF (mécanisme réel = réduction structurelle
// modulée, à confirmer ONSS — déjà flaggé dans la note). UNE seule vie pour
// le calcul et la note ; valeur inchangée le 20/08/2026.
const TX_APPRENTI_CCT1 = 0.0349;
  const brutR = r2(brut * (regime / 100));

  switch (statut) {

    // ── EMPLOYÉ STANDARD ──────────────────────────────────
    case 'employe': {
      const onssW = r2(brutR * TX_ONSS_W);
      // ═══ DÉLÉGATION au moteur certifié (26/07/2026) ═══
      // Avant, cette branche recalculait le précompte elle-même, avec QUATRE
      // défauts : base `brutImp` alors que calcPrecompteExact attend le BRUT et
      // retire l'ONSS lui-même (donc ONSS déduit deux fois), `situationFamiliale`
      // jamais transmise, `enfants` jamais transmis, et AUCUN bonus à l'emploi.
      // Écart constaté : −230 € de net à 2 233 €, +246 € à 4 500 €.
      // On ne recopie pas la recette du moteur — on l'appelle. La parité devient
      // vraie par construction. Le moteur est vérifié contre la formule-clé
      // officielle du SPF (ESS-SR/2025-0928, 01/01/2026) : à 3 000 € isolé,
      // 466,68 € puis 436,10 € après la réduction bas salaires de 33,14 %.
      // brutR est déjà proratisé → on passe regime 100 pour ne pas l'appliquer deux fois.
      const REF = calcPayroll(brutR, 'employe', situationFamiliale, enfants, 100);
      const brutImp = REF.imposable;
      const pp = REF.pp;
      const css = REF.csss;
      const werkbonus = REF.werkbonus;
      const net = REF.net;
      const onssE = r2(brutR * TX_ONSS_E);
      // 🔴 CORRECTIF 2026-09-11 (cf. calcCoutEmployeurFromBrut) : `at` reste dans
      // `coutTotal`, mais dérivé de TX_AT_ASSURANCE (assurance-loi AT privée,
      // Loi du 10/04/1971, obligatoire et distincte de l'ONSS) au lieu de TX_AT
      // (0,97%, approximation invérifiable de la même prime). Le retirer purement
      // avait fait disparaître un vrai coût et divergé du générateur de fiches.
      const at = r2(brutR * TX_AT_ASSURANCE);
      const coutTotal = r2(brutR + onssE + at);
      return { statut, brutR, onssW, werkbonus, onssE, at, brutImp, pp, css, net, coutTotal,
        fiche: '281.10', label: 'Employé', onssBase: brutR };
    }

    // ── OUVRIER ───────────────────────────────────────────
    case 'ouvrier': {
      const base108 = r2(brutR * TX_OUV108);
      const onssW = r2(base108 * TX_ONSS_W);
      // ONVA vacances annuelles — cotisation PATRONALE ouvriers.
      // ⚠️ CORRECTIF 2026-07-28 (routine « chasse bugs paie ») : la base était brutR
      // (100 %) alors que le registre lois-belges.js:371 documente « brut ouvrier
      // annuel x 108% » (VBO-FEB Q1 2026 + ONSS), et que les DEUX autres
      // consommateurs appliquent bien 108 % (calcCoutEmployeurFromBrut:546 via baseE,
      // ChargesSalariales.js). calcPayrollStatut('ouvrier').coutTotal sous-évaluait
      // donc l'ONVA de ~1,27 % du brut (2 500 € → 31,68 €/mois) face à
      // coutEmployeurComplet du même brut. Le taux en dur violait aussi R1.
      // N'affecte QUE coutTotal : onssVac n'entre pas dans le net (cf. l.912), donc
      // les validations 29/29 Liantis — qui ne couvraient que le net — sont intactes.
      const txVacOuv = (LOIS_BELGES?.remuneration?.peculeVacances?.ouvrierPatronal?.pct) || 0.1584;
      const onssVac = r2(base108 * txVacOuv);
      // DÉLÉGATION au moteur certifié — cf. branche 'employe'. Le moteur gère
      // lui-même la base ONSS à 108 % de l'ouvrier.
      const REF = calcPayroll(brutR, 'ouvrier', situationFamiliale, enfants, 100);
      const brutImp = REF.imposable;
      const pp = REF.pp;
      const css = REF.csss;
      const werkbonus = REF.werkbonus;
      const net = REF.net;
      const onssE = r2(brutR * TX_ONSS_E);
      // 🔴 CORRECTIF 2026-09-11 : même correction que la branche 'employe' juste
      // au-dessus — `at` dérivé de TX_AT_ASSURANCE, pas retiré de `coutTotal`.
      const at = r2(brutR * TX_AT_ASSURANCE);
      const coutTotal = r2(brutR + onssE + at + onssVac);
      return { statut, brutR, onssW, werkbonus, onssVac, onssE, at, base108, brutImp, pp, css, net, coutTotal,
        fiche: '281.10', label: 'Ouvrier', note: 'Base ONSS = brut × 108%' };
    }

    // ── ÉTUDIANT JOBISTE ──────────────────────────────────
    // Quota 2026: 650h/an (depuis 1er janvier 2025, gouvernement Arizona)
    // Source: ONSS, studentatwork.be, Securex 2026
    case 'etudiant': {
      const heuresAnn = params.heuresAnnuelles || 0;
      const PLAFOND_HEURES = LOIS_BELGES.cotisations.etudiant.heuresMax;
      const dansPlafond = heuresAnn <= PLAFOND_HEURES;
      const onssW = dansPlafond ? r2(brutR * LOIS_BELGES.cotisations.etudiant.travailleur) : r2(brutR * TX_ONSS_W);
      const onssE = dansPlafond ? r2(brutR * LOIS_BELGES.cotisations.etudiant.patronal) : r2(brutR * TX_ONSS_E);
      const cotisTotal = dansPlafond ? r2(brutR * LOIS_BELGES.cotisations.etudiant.total) : r2(brutR * 0.3814);
      const brutImp = r2(brutR - onssW);
      // ⏸️ 02/09/2026 : on passe le BRUT + l'ONSS réellement retenu. Avant, on passait un montant déjà net → ONSS déduit deux fois → précompte trop bas (même défaut que le dirigeant, corrigé le 20/07 sur cette seule surface — R2 ter-C).
      const pp = dansPlafond ? 0 : r2(calcPrecompteExact(brutR, { onssRetenu: onssW }).pp);
      const net = r2(brutImp - pp);
      const coutTotal = r2(brutR + onssE);
      return { statut, brutR, onssW, onssE, cotisTotal, brutImp, pp, css: 0, net, coutTotal,
        fiche: '281.10', label: 'Étudiant', dansPlafond,
        heuresRestantes: Math.max(0, PLAFOND_HEURES - heuresAnn),
        plafondHeures: PLAFOND_HEURES,
        note: dansPlafond ? `Cotisation solidarité 8,13% — dans les ${PLAFOND_HEURES}h` : `⚠️ Hors plafond ${PLAFOND_HEURES}h → ONSS normal` };
    }

    // ── FLEXI-JOB ─────────────────────────────────────────
    // ⚠️ ZONE PROTÉGÉE (K3) — modifié le 03/09/2026 avec heads-up explicite,
    // jamais en auto-merge.
    //
    // Ce bloc recopiait SIX constantes légales en dur : 11,87 €/h (deux fois),
    // 18 440, 8 121, 0,28 et 0,0767. Le SPF a indexé le flexi-salaire de 2 % le
    // 01/09/2026 → 12,11 €/h. Les cinq autres étaient encore justes ; le
    // 11,87 ne l'était plus, et il sert de TAUX PAR DÉFAUT quand l'appelant
    // n'en passe pas — donc un calcul sous le plancher légal, en silence.
    // Les six lisent désormais `LOIS_BELGES.cotisations.flexiJob`, que ce
    // fichier importait déjà (la ligne `note:` en dessous l'utilisait).
    // Source : fiche SPF CP 302, section 1.2.1, relue le 03/09/2026.
    case 'flexijob': {
      const _FX = LOIS_BELGES.cotisations.flexiJob;
      const tauxH = params.tauxHoraire || _FX.salaireMin;
      const isPensionAnticipee = params.pensionAnticipee === true;
      const plafondAnnuel = isPensionAnticipee ? _FX.plafondPensionAnticipee : _FX.plafond;
      const onssW = 0;
      const onssE = r2(brutR * _FX.patronal);
      const peculeVacances = r2(brutR * _FX.peculeVacances); // versé annuellement
      const pp = 0;
      const css = 0;
      const net = brutR; // travailleur reçoit le brut complet
      const coutTotal = r2(brutR + onssE);
      return { statut, brutR, onssW, onssE, peculeVacances, pp, css, net, coutTotal,
        fiche: '281.10', label: 'Flexi-job',
        note: `Pas de cotisation travailleur ni PP — cotisation patronale ${_pctNote(LOIS_BELGES.cotisations.flexiJob.patronal)}% + pécule ${_pctNote(LOIS_BELGES.cotisations.flexiJob.peculeVacances)}% annuel`,
        plafondAnnuel, tauxHMin: _FX.salaireMin, tauxAppliquee: tauxH,
        pensionAnticipee: isPensionAnticipee };
    }

    // ── DIRIGEANT / GÉRANT RÉMUNÉRÉ ───────────────────────
    case 'dirigeant': {
      // Le forfait frais pro 3% (plafond LB.pp.fraisPro.dirigeant.max/an — SPF Finances,
      // revenus 2026 = 3.200€/an) est appliqué UNE SEULE fois, sur la base FISCALE, à
      // l'intérieur de calcPrecompteExact(dirigeant:true). On ne le pré-déduit donc PLUS
      // ici (sinon double comptage → précompte trop bas). Le forfait réduit la base
      // FISCALE, pas le cash encaissé → net = brut − précompte (et non brut − forfait − pp).
      // CORR 2026-07-20 : (R1) plus de 0.03 / 3200 en dur + fin du double forfait + net réel.
      const forf = LOIS_BELGES.pp.fraisPro.dirigeant;
      // Cotisation INASTI prise en charge par la société ET attribuée au moins une fois par
      // mois = rémunération PÉRIODIQUE imposable → entre dans la base du précompte mensuel
      // (AR/CIR 92, Annexe III, n°17). Défaut 0 → certification 281.20 (GOLDEN) inchangée.
      const cotisInastiPC = Math.max(0, +(params.cotisInastiPriseEnCharge) || 0);
      // Situation familiale + enfants à charge : sans ces deux-là, le précompte du
      // dirigeant était TOUJOURS calculé « isolé, 0 enfant » (l'écran Rémunérations
      // tous statuts ne les transmettait pas). Même mapping que calcPayroll.
      // Défaut 'isole'/0 → base FISCALE identique → certification 281.20 GOLDEN inchangée.
      let _sitDir = 'isole';
      if (situationFamiliale === 'marie_1rev' || situationFamiliale === 'marie_1r') _sitDir = 'marie_1r';
      else if (situationFamiliale === 'marie_2rev' || situationFamiliale === 'marie_2r') _sitDir = 'marie_2r';
      else if (situationFamiliale === 'cohabitant') _sitDir = 'cohabitant';
      const pp = r2(calcPrecompteExact(brutR + cotisInastiPC, { dirigeant: true, situation: _sitDir, enfants }).pp);
      const fraisForf = r2(Math.min(brutR * forf.pct, forf.max / 12)); // informatif (affichage fiche)
      const imposable = r2(brutR + cotisInastiPC - fraisForf); // base fiscale — informatif, n'entre pas dans le net
      const net = r2(brutR - pp); // l'INASTI prise en charge n'est pas du cash → net = brut − précompte
      const coutTotal = brutR; // pas d'ONSS patronal pour dirigeant
      return { statut, brutR, fraisForf, imposable, onssW: 0, onssE: 0, pp, css: 0, net, coutTotal,
        cotisInastiPriseEnCharge: cotisInastiPC,
        fiche: '281.20', label: 'Dirigeant d\'entreprise',
        note: 'Pas d\'ONSS salarié — frais forfaitaires 3% (max ' + r2(forf.max / 12) + '€/mois)' };
    }

    // ── JETONS DE PRÉSENCE (Administrateur non exécutif) ──
    case 'jeton': {
      const pp = r2(brutR * 0.30);
      const net = r2(brutR - pp);
      return { statut, brutR, onssW: 0, onssE: 0, pp, css: 0, net, coutTotal: brutR,
        fiche: '281.17', label: 'Jeton de présence / Administrateur',
        note: 'Retenue PP 30% à la source — fiche 281.17' };
    }

    // ── ASSOCIÉ ACTIF ─────────────────────────────────────
    case 'associe_actif': {
      // Cotisations sociales indépendant (~20,5% sur revenu net imposable) — taux registre R1
      const cotSoc = r2(brutR * LOIS_BELGES.cotisations.independants.principal.tranche1);
      const brutImp = r2(brutR - cotSoc);
      const pp = r2(brutImp * 0.25); // versements anticipés estimés
      const net = r2(brutImp - pp);
      return { statut, brutR, cotSoc, brutImp, pp, css: 0, onssW: 0, onssE: 0, net, coutTotal: brutR,
        fiche: '281.50', label: 'Associé actif',
        note: 'Cotisations sociales indépendant via caisse sociale — PP via versements anticipés' };
    }

    // ── ARTISTE ───────────────────────────────────────────
    case 'artiste': {
      const petitePrestation = (params.prestations || 0) <= 30;
      if (petitePrestation) {
        const indemnite = r2(Math.min(brutR, LOIS_BELGES.cotisations.artiste.exonerationJournaliere));
        return { statut, brutR, onssW: 0, onssE: 0, pp: 0, css: 0, net: indemnite, coutTotal: indemnite,
          fiche: '281.50', label: 'Artiste (petite prestation)',
          note: '≤30 prestations/an — exonération ONSS jusqu\'à 75,84€/jour' };
      }
      const onssW = r2(brutR * TX_ONSS_W);
      const brutImp = r2(brutR - onssW);
      // ⏸️ 02/09/2026 : on passe le BRUT + l'ONSS réellement retenu. Avant, on passait un montant déjà net → ONSS déduit deux fois → précompte trop bas (même défaut que le dirigeant, corrigé le 20/07 sur cette seule surface — R2 ter-C).
      const pp = r2(calcPrecompteExact(brutR, { onssRetenu: onssW }).pp);
      const net = r2(brutImp - pp);
      const onssE = r2(brutR * TX_ONSS_E);
      return { statut, brutR, onssW, onssE, brutImp, pp, css: 0, net, coutTotal: r2(brutR + onssE),
        fiche: '281.10', label: 'Artiste (régime salarié)',
        note: '>30 prestations/an → ONSS salarié normal' };
    }

    // ── SPORTIF ───────────────────────────────────────────
    case 'sportif': {
      const annuelEstime = brutR * 12;
      const exonere = annuelEstime <= LOIS_BELGES.cotisations.sportif.seuilExoneration;
      if (exonere) {
        return { statut, brutR, onssW: 0, onssE: 0, pp: 0, css: 0, net: brutR, coutTotal: brutR,
          fiche: '281.10', label: 'Sportif (seuil exonération)',
          note: 'Revenus annuels ≤ 12.300€ — exonération ONSS et PP' };
      }
      const onssW = r2(brutR * TX_ONSS_W);
      const brutImp = r2(brutR - onssW);
      // ⏸️ 02/09/2026 : on passe le BRUT + l'ONSS réellement retenu. Avant, on passait un montant déjà net → ONSS déduit deux fois → précompte trop bas (même défaut que le dirigeant, corrigé le 20/07 sur cette seule surface — R2 ter-C).
      const pp = r2(calcPrecompteExact(brutR, { onssRetenu: onssW }).pp);
      const net = r2(brutImp - pp);
      const onssE = r2(brutR * TX_ONSS_E);
      return { statut, brutR, onssW, onssE, brutImp, pp, css: 0, net, coutTotal: r2(brutR + onssE),
        fiche: '281.10', label: 'Sportif', note: 'Revenus > 12.300€/an → régime normal' };
    }

    // ── VOLONTAIRE ────────────────────────────────────────
    case 'volontaire': {
      // Q2 2026 CONFIRMÉ — Sources: Securex 15/01/2026, Acerta 21/01/2026, ONSS Instructions 2026/1
      // Plafonds 2026: 44,02€/jour · 1 760,83€/an (standard) · 3 233,91€/an (catégorie majorée)
      // Catégorie majorée = sport (entraîneur, coach, arbitre) + garde de personnes + transport médical
      const daysMonth = params.jours || 20;
      const categorieMajoree = params.categorieMajoree === true;
      // R1 : plafonds lus depuis lois-belges.js (source unique), plus de valeurs en dur.
      const V = LOIS_BELGES.cotisations.volontaire;
      const PLAFOND_JOUR = V.plafondJournalier;
      const PLAFOND_AN = categorieMajoree ? V.plafondAnnuelMajore : V.plafondAnnuel;
      const totalMois = r2(daysMonth * PLAFOND_JOUR);
      const plafondMois = r2(PLAFOND_AN / 12);
      const imposable = Math.max(0, r2(totalMois - plafondMois));
      // ⏸️ 02/09/2026 : `imposable` est la part au-dessus du plafond d'exemption,
      // pas un brut — et un volontaire ne cotise PAS à l'ONSS. On le dit donc
      // explicitement plutôt que de laisser la fonction en retenir 13,07 %.
      const pp = imposable > 0 ? r2(calcPrecompteExact(imposable, { onssRetenu: 0 }).pp) : 0;
      const net = r2(totalMois - pp);
      return { statut, brutR: totalMois, onssW: 0, onssE: 0, pp, css: 0, net, coutTotal: totalMois,
        fiche: imposable > 0 ? '281.10' : null, label: 'Volontaire',
        plafondJour: PLAFOND_JOUR, plafondAnnuel: PLAFOND_AN,
        note: imposable > 0 ? `⚠️ Dépassement plafond — ${imposable}€ imposable` : '✅ Dans les plafonds — non imposable' };
    }

    // ── ARTICLE 17 (Sport / Socioculturel) ─────────────────
    // Q2 2026: 0% cotisations sociales si dans plafonds + taxation 10% (revenus divers)
    // Plafond fiscal: 7 700€/an (revenus 2025/exercice 2026, à indexer pour 2026)
    // Plafonds horaires: sport 450h/an, socio 300h/an, étudiant cumul 190h/an
    // Source: Article 17 AR 28/11/1969 + SPF Emploi + CESSoC
    case 'article17': {
      const heuresAnnuelles = params.heuresAnnuelles || 0;
      const secteur = params.secteur || 'socio'; // 'sport' | 'socio'
      const estEtudiant = params.estEtudiant === true;
      let plafondHeures = secteur === 'sport' ? 450 : 300;
      if (estEtudiant) plafondHeures = 190;
      const dansPlafond = heuresAnnuelles <= plafondHeures;
      // Plafond fiscal Q2 2026 confirmé: 7 700€ pour revenus 2025/exercice 2026
      // Pour revenus 2026/exercice 2027, indexation à confirmer fin 2026 (~+2,47% Wolters Kluwer)
      const PLAFOND_FISCAL = 7700;
      const taxeRevenus = dansPlafond ? r2(brutR * 0.10) : 0;
      const onssW = dansPlafond ? 0 : r2(brutR * TX_ONSS_W);
      const onssE = dansPlafond ? 0 : r2(brutR * TX_ONSS_E);
      // ⏸️ 02/09/2026 : on passe le BRUT + l'ONSS réellement retenu. Avant, on passait un montant déjà net → ONSS déduit deux fois → précompte trop bas (même défaut que le dirigeant, corrigé le 20/07 sur cette seule surface — R2 ter-C).
      const pp = dansPlafond ? 0 : r2(calcPrecompteExact(brutR, { onssRetenu: onssW }).pp);
      const net = dansPlafond ? r2(brutR - taxeRevenus) : r2(brutR - onssW - pp);
      const coutTotal = r2(brutR + onssE);
      return { statut, brutR, onssW, onssE, taxeRevenus, pp, css: 0, net, coutTotal,
        fiche: '281.10', label: 'Article 17 (Sport/Socio)',
        secteur, dansPlafond, plafondHeures, plafondFiscal: PLAFOND_FISCAL,
        heuresRestantes: Math.max(0, plafondHeures - heuresAnnuelles),
        dimona: secteur === 'sport' ? 'S17' : 'O17',
        note: dansPlafond
          ? `✅ Dans plafond ${plafondHeures}h — 0% cotis + taxe 10% revenus divers`
          : `⚠️ Hors plafond ${plafondHeures}h — requalification ONSS normal` };
    }

    // ── APPRENTI / FORMATION EN ALTERNANCE ─────────────────
    // Q2 2026: cotisations ONSS réduites pour apprentis
    // - CCT n°1 (apprenti industriel) : ONSS travailleur exonéré jusqu'à 18 ans
    //   puis cotisation réduite. Patronal : ~3,49% au lieu de 25,07%
    // - IFAPME / EFPME / Syntra : ONSS solidarité 8,13% si conditions
    // - PFI (Actiris / VDAB / Forem) : indemnité non assujettie ONSS
    // Source: AR 28/11/1969 art. 5 + Décrets régionaux
    case 'apprenti': {
      const typeAlt = params.typeAlternance || 'cct1';
      const annee = params.anneeAppr || 1;
      let onssW, onssE, pp;
      if (typeAlt === 'pfi') {
        // PFI : exonération totale (indemnité de stage)
        onssW = 0; onssE = 0; pp = 0;
      } else if (typeAlt === 'ifapme') {
        // IFAPME : solidarité réduite 8,13%
        onssW = r2(brutR * LOIS_BELGES.cotisations.etudiant.travailleur);
        onssE = r2(brutR * LOIS_BELGES.cotisations.etudiant.patronal);
        pp = 0;
      } else {
        // Q2 2026 — Règle ONSS apprentis (Instructions 2025/4 + 2026/1):
        //   < 19 ans : assujettissement LIMITÉ (pas modération salariale, certains régimes)
        //   ≥ 19 ans : cotisations ordinaires
        const moinsDe19 = params.moinsDe19 === true || params.moinsDe18 === true;
        if (moinsDe19) {
          onssW = 0; // assujettissement limité
          onssE = r2(brutR * TX_APPRENTI_CCT1); // taux groupe-cible jeune — MÊME constante que la note (valeur inchangée)
          pp = 0;
        } else {
          onssW = r2(brutR * TX_ONSS_W);
          onssE = r2(brutR * TX_ONSS_E);
          // ⏸️ 02/09/2026 : on passe le BRUT + l'ONSS réellement retenu. Avant, on passait un montant déjà net → ONSS déduit deux fois → précompte trop bas (même défaut que le dirigeant, corrigé le 20/07 sur cette seule surface — R2 ter-C).
          pp = r2(calcPrecompteExact(brutR, { onssRetenu: onssW }).pp);
        }
      }
      const net = r2(brutR - onssW - pp);
      const coutTotal = r2(brutR + onssE);
      return { statut, brutR, onssW, onssE, pp, css: 0, net, coutTotal,
        fiche: '281.10', label: 'Apprenti', typeAlternance: typeAlt, anneeAppr: annee,
        note: typeAlt === 'pfi'
          ? '✅ PFI — Indemnité stage non assujettie ONSS'
          : typeAlt === 'ifapme'
            ? `✅ IFAPME — Cotisation solidarité ${_pctNote(LOIS_BELGES.cotisations.etudiant.total)}%`
            : `⚠️ CCT n°1 — ONSS patronal ~${_pctNote(TX_APPRENTI_CCT1)}% (taux indicatif, mécanisme réel = réduction structurelle modulée, à confirmer ONSS)` };
    }

    // ── EXTRAS HORECA — TRAVAILLEUR OCCASIONNEL ─────────────
    // Q1/Q2 2026: forfaits indexés (Instruction ONSS 2025/4)
    // - Forfait horaire: 10,67€/h (max 5h, plafond 64,02€/jour)
    // - Forfait journalier: 64,02€/jour (>5h)
    // - Contingent: 50j/an travailleur, 200j/an employeur
    // - Précompte spécifique Horeca: 33,31% (Dimona EXT)
    // Source: AR 28/11/1969 + ONSS Instruction 2025/4
    case 'extras_horeca': {
      const mode = params.modeDimona || 'horaire'; // 'horaire' | 'journalier' | 'ext'
      const _HEX = LOIS_BELGES.horeca.extras; // forfaits/contingent/taux = registre (R1)
      const heures = params.heures || 0;
      const jours = params.jours || 1;
      const joursAnnuels = params.joursAnnuels || 0;
      const dansContingent = joursAnnuels <= _HEX.contingent_travailleur;
      let forfait;
      if (mode === 'horaire') {
        forfait = Math.min(r2(heures * _HEX.forfait_horaire), _HEX.forfait_journalier * jours);
      } else if (mode === 'journalier') {
        forfait = r2(jours * _HEX.forfait_journalier);
      } else { // ext
        forfait = brutR;
      }
      const onssW = dansContingent ? r2(forfait * TX_ONSS_W) : r2(brutR * TX_ONSS_W);
      const onssE = dansContingent ? r2(forfait * TX_ONSS_E) : r2(brutR * TX_ONSS_E);
      const ppTaux = mode === 'ext' ? _HEX.taux_pp : 0.18;
      const pp = r2((brutR - onssW) * ppTaux);
      const net = r2(brutR - onssW - pp);
      const coutTotal = r2(brutR + onssE);
      return { statut, brutR, onssW, onssE, pp, css: 0, net, coutTotal,
        fiche: '281.10', label: 'Extras Horeca occasionnel', mode,
        forfaitBase: forfait, dansContingent,
        joursRestants: Math.max(0, _HEX.contingent_travailleur - joursAnnuels),
        note: dansContingent
          ? `✅ Forfait ${mode}: ${forfait}€ — Dans contingent 50j (Q1 2026: 10,67€/h ou 64,02€/j)`
          : `⚠️ Hors contingent 50j — cotisations sur brut réel` };
    }

    // ── INTÉRIMAIRE ───────────────────────────────────────
    // Deux modes:
    //  - params.agenceMode === true  → l'utilisateur EST une agence d'intérim CP 322
    //                                   (Nestor, etc.) → délégation au moteur dédié
    //  - sinon                       → l'utilisateur EMBAUCHE un intérimaire via une
    //                                   agence externe → calcul ONSS standard
    case 'interimaire': {
      if (params.agenceMode === true) {
        // Lazy import pour éviter cycle de dépendance
        const { calcSalaireInterimCP322 } = require('./interim-cp322-engine')
        const result = calcSalaireInterimCP322({
          brutMensuel: brutR,
          heures: params.heures || 0,
          anciennete: params.anciennete || 0,
          precompteContext: params.precompteContext || {},
        })
        return { ...result, statut } // garde la cohérence du statut original
      }
      const onssW = r2(brutR * TX_ONSS_W);
      // DÉLÉGATION au moteur certifié (26/07/2026) — cf. commentaire de la branche
      // 'employe'. Ces statuts sont des salariés ordinaires côté paie.
      const REF = calcPayroll(brutR, 'employe', situationFamiliale, enfants, 100);
      const brutImp = REF.imposable;
      const pp = REF.pp;
      const css = REF.csss;
      const werkbonus = REF.werkbonus;
      const net = REF.net;
      const onssE = r2(brutR * TX_ONSS_E);
      // 🔴 CORRECTIF 2026-09-11 : même correction que la branche 'employe'.
      const at = r2(brutR * TX_AT_ASSURANCE);
      const coutTotal = r2(brutR + onssE + at);
      return { statut, brutR, onssW, werkbonus, onssE, at, brutImp, pp, css, net, coutTotal,
        fiche: '281.10', label: 'Intérimaire',
        note: 'ONSS standard — déclaration via agence d\'intérim (Dimona OTH)' };
    }

    // ── CONJOINT AIDANT ───────────────────────────────────
    case 'conjoint_maxi': {
      // INASTI 2026 — Conjoint aidant maxi-statut. Plafonds/taux du registre (R1).
      // Mêmes taux qu'indépendant titre principal (20,5% / 14,16% / 0% au-delà).
      // Cotisation provisoire min conjoint aidant maxi 2026 : 407,59€/trim.
      const _IND = LOIS_BELGES.cotisations.independants;
      const brutAnnuel = brutR * 12;
      const PLAFOND_INTER = _IND.plafonds.intermediaire;
      const PLAFOND_ABSOLU = _IND.plafonds.absolu;
      const tranche1 = Math.min(brutAnnuel, PLAFOND_INTER);
      const tranche2 = Math.max(0, Math.min(brutAnnuel, PLAFOND_ABSOLU) - PLAFOND_INTER);
      let cotAnnuelle = tranche1 * _IND.principal.tranche1 + tranche2 * _IND.principal.tranche2;
      // Cotisation minimum
      const COT_MIN_AN = _IND.conjointMaxi.provisionnelMinTrim * 4;
      cotAnnuelle = Math.max(cotAnnuelle, COT_MIN_AN);
      const cotSoc = r2(cotAnnuelle / 12);
      const net = r2(brutR - cotSoc);
      return { statut, brutR, cotSoc, onssW: 0, onssE: 0, pp: 0, css: 0, net, coutTotal: brutR,
        fiche: '281.50', label: 'Conjoint aidant (maxi-statut)',
        note: `2026: ${_pctNote(_IND.principal.tranche1)}% / ${_pctNote(_IND.principal.tranche2)}% selon plafonds INASTI · cot. min ${_IND.conjointMaxi.provisionnelMinTrim.toFixed(2).replace('.', ',')}€/trim · droits sociaux complets indépendant` };
    }

    case 'conjoint_mini': {
      // INASTI 2026 — Conjoint aidant mini-statut (incapacité uniquement). Plafonds/taux du registre (R1).
      //   - 0,79% / 0,51% selon tranches (mêmes plafonds que le principal)
      const _IND = LOIS_BELGES.cotisations.independants;
      const brutAnnuel = brutR * 12;
      const SEUIL_MIN = _IND.conjointMini.seuilMin;
      const PLAFOND_INTER = _IND.plafonds.intermediaire;
      const PLAFOND_ABSOLU = _IND.plafonds.absolu;
      const baseCalc = Math.max(brutAnnuel, SEUIL_MIN);
      const tranche1 = Math.min(baseCalc, PLAFOND_INTER) - SEUIL_MIN;
      const tranche2 = Math.max(0, Math.min(baseCalc, PLAFOND_ABSOLU) - PLAFOND_INTER);
      const cotAnnuelle = Math.max(0, tranche1) * _IND.conjointMini.tranche1 + tranche2 * _IND.conjointMini.tranche2;
      const cotForf = r2(cotAnnuelle / 12);
      const net = r2(brutR - cotForf);
      return { statut, brutR, cotForf, onssW: 0, onssE: 0, pp: 0, css: 0, net, coutTotal: brutR,
        fiche: '281.50', label: 'Conjoint aidant (mini-statut)',
        note: `Q2 2026: ${_pctNote(_IND.conjointMini.tranche1)}% / ${_pctNote(_IND.conjointMini.tranche2)}% selon tranches INASTI · couverture incapacité de travail uniquement` };
    }

    // ── PENSIONNÉ ACTIF ───────────────────────────────────
    case 'pensionneActif': {
      const age = params.age || 65;
      // Plafond de cumul (revenus autorisés avant l'âge légal) = registre (R1).
      // L'ancienne valeur 2025 codée en dur était PÉRIMÉE → 8.346€ (2026, INASTI). null = pas de plafond ≥ 65 ans.
      const plafond = age >= 65 ? null : LOIS_BELGES.cotisations.pensionneActif.plafondAvant65;
      const brutAnnuel = brutR * 12;
      const depassement = plafond ? Math.max(0, r2(brutAnnuel - plafond)) : 0;
      const onssW = r2(brutR * TX_ONSS_W);
      const brutImp = r2(brutR - onssW);
      // ⏸️ 02/09/2026 : on passe le BRUT + l'ONSS réellement retenu. Avant, on passait un montant déjà net → ONSS déduit deux fois → précompte trop bas (même défaut que le dirigeant, corrigé le 20/07 sur cette seule surface — R2 ter-C).
      const pp = r2(calcPrecompteExact(brutR, { onssRetenu: onssW }).pp);
      const net = r2(brutImp - pp);
      const onssE = r2(brutR * TX_ONSS_E);
      return { statut, brutR, onssW, onssE, brutImp, pp, css: 0, net, coutTotal: r2(brutR + onssE),
        fiche: '281.10', label: 'Pensionné actif',
        plafond, depassementAnnuel: depassement,
        note: age >= 65 ? '≥65 ans — pas de plafond de cumul' : `<65 ans — plafond annuel ${plafond?.toLocaleString('fr-BE')}€ | dépassement: ${depassement}€` };
    }

    // ── APE (Aide Promotion Emploi) ───────────────────────
    case 'APE': {
      const niveau = params.niveauAPE || 1;
      const subventions = [0, 5000, 10000, 15000, 20000, 25000, 30000, 35000, 38000];
      const subvMensuelle = r2((subventions[niveau] || 0) / 12);
      const onssW = r2(brutR * TX_ONSS_W);
      // DÉLÉGATION au moteur certifié (26/07/2026) — cf. commentaire de la branche
      // 'employe'. Ces statuts sont des salariés ordinaires côté paie.
      const REF = calcPayroll(brutR, 'employe', situationFamiliale, enfants, 100);
      const brutImp = REF.imposable;
      const pp = REF.pp;
      const css = REF.csss;
      const werkbonus = REF.werkbonus;
      const net = REF.net;
      const onssE = r2(brutR * TX_ONSS_E);
      const coutReel = r2(brutR + onssE - subvMensuelle);
      return { statut, brutR, onssW, werkbonus, onssE, brutImp, pp, css, net,
        coutTotal: r2(brutR + onssE), coutReel, subvMensuelle,
        fiche: '281.10', label: `APE niveau ${niveau}`,
        note: `Subvention Bruxelles ${subventions[niveau]?.toLocaleString('fr-BE')}€/an → coût réel employeur: ${coutReel}€/mois` };
    }

    // ── ACS (Agent Contractuel Subventionné) ──────────────
    case 'ACS': {
      const subvMensuelle = 900;
      const onssW = r2(brutR * TX_ONSS_W);
      // DÉLÉGATION au moteur certifié (26/07/2026) — cf. commentaire de la branche
      // 'employe'. Ces statuts sont des salariés ordinaires côté paie.
      const REF = calcPayroll(brutR, 'employe', situationFamiliale, enfants, 100);
      const brutImp = REF.imposable;
      const pp = REF.pp;
      const css = REF.csss;
      const werkbonus = REF.werkbonus;
      const net = REF.net;
      const onssE = r2(brutR * TX_ONSS_E);
      const coutReel = r2(brutR + onssE - subvMensuelle);
      return { statut, brutR, onssW, werkbonus, onssE, brutImp, pp, css, net,
        coutTotal: r2(brutR + onssE), coutReel, subvMensuelle,
        fiche: '281.10', label: 'ACS (non-marchand)',
        note: `Subvention mensuelle 900€ — coût réel: ${coutReel}€/mois` };
    }

    default:
      return null;
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// calcCSS SUPPRIMÉE le 26/07/2026 — copie à tranches CODÉES EN DUR (violation R1).
//
// Elle vivait ici depuis longtemps, appelée par 5 branches de calcPayrollStatut
// (employe, ouvrier, interimaire, APE, ACS), avec des tranches introuvables dans
// lois-belges.js : 0 / 9,30 € / 2,2 % / 1,1 %. Elle prenait l'IMPOSABLE mensuel
// là où le barème ONSS est TRIMESTRIEL, ignorait la situation familiale (3 régimes
// distincts) et la majoration ouvrier à 108 %. Résultat, elle sur-retenait
// massivement (55,00 € au lieu de 13,74 € pour 2 500 € de brut isolé) et n'était
// même pas monotone (132,85 € à 6 038 €, puis 77,00 € à 7 000 €).
//
// Les 5 appelants passent désormais par calcCSSS (ci-dessus, ligne ~227), qui lit
// les barèmes de lois-belges.js, distingue isolé / ménage 1 revenu / ménage
// 2 revenus, et applique la majoration ouvrier.
//
// Verrou : __tests__/csss-parite-implementations.test.js
// ═══════════════════════════════════════════════════════════════════════════
// Cache-bust: Wed Apr  1 12:32:13 UTC 2026
